// Product Ads — product-centric pivot of the Ads page.
//
// Each row is a product; clicking expands inline to show the campaign
// list + ads grid generated for that product. KPI tiles up top
// summarize the brand-wide picture. Bulk selection + "Generate Ads"
// deep-links to the wizard with productIds pre-populated.
//
// Phase 1 scope:
//   - Backend aggregation via GET /api/catalog/ads-summary
//   - Inline expansion via GET /api/catalog/:id/ads-detail
//   - Per-row Generate Ads → /generate-ads?productIds=X
//   - Bulk select → /generate-ads?productIds=A,B,C
//
// Deferred to Phase 2:
//   - Opportunity score column + sort
//   - "Generate for new opportunities" / "low coverage" buckets
//   - Automation rules

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardBody, VStack, HStack, Text, Spinner, Button, ButtonGroup,
  Image, Badge, Progress, Checkbox, SimpleGrid, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, Select, Wrap, WrapItem, Input,
  Textarea, Accordion, AccordionItem, AccordionButton, AccordionPanel,
  AccordionIcon, Link
} from '@chakra-ui/react';

import { PageHeader } from '../../shell/PageHeader';
import { apiJson } from '../../auth/apiFetch';
import { useBrand } from '../../brand/BrandContext';
import { useSetAgentContext } from '../../agent';
import { GenerationInspectorModal, type InspectorData } from '../../components/GenerationInspectorModal';
import { ModalErrorBoundary } from '../../components/ModalErrorBoundary';
import { HoverPlayVideo } from '../../components/HoverPlayVideo';
import { funnelStageLabel, mediaTypeLabel } from '../../components/adLabels';
import {
  AdChromeFrame, AdChromePreview, ChromeSelect,
  normalizeRatio, useAdChrome, useChromeIdentity,
  QcFailureReview, qcReviewDisplayUrl
} from '../../components/adChrome';
import { adStatusBadgeColorScheme, resolveAdStatusBadge } from '../Ads';
import { toExpansionAd } from '../../lib/expansionAd';
import {
  RETITLE_FOLLOW_MAX_MS, isSceneReelAd, retitleOnlyPending, sceneReelBusy, toSceneReelSummary, type SceneReelSummary
} from '../../lib/sceneReel';
import { shouldPaintQcOutline } from '../../lib/qcAdvisoryOutline';
import { safeHttpUrl } from '../../lib/safeHref';
import { QcFindingsPanel } from './QcFindingsPanel';
import { SceneReelBadge } from './SceneReelBadge';
import { SceneStrip } from './SceneStrip';
import { interpretMetaPush, type MetaPushResponse, type MetaPushTone } from './metaPush';
export { toExpansionAd };

type CampaignChip = {
  id:     string;
  name:   string;
  kind:   string | null;
  status: string | null;
};

type ProductRow = {
  productId:        string;
  title:            string;
  price:            number | null;
  currency:         string | null;
  imageUrl:         string | null;
  category:         string | null;
  brand:            string | null;
  size:             string | null;
  adCount:          number;
  campaignCount:    number;
  campaignChips?:   CampaignChip[];
  readyToExport:    number;
  draftCount:       number;
  liveCount:        number;
  coveragePct:      number;
  // Outcome split from the coverage fix; optional so the row renders unchanged
  // against a backend that predates it.
  deliveredCount?:  number;
  failedCount?:     number;
  inFlightCount?:   number;
  opportunityScore: number | null;
  lastActivityAt:   string | null;
};

type Summary = {
  totalProducts:    number;
  productsWithAds:  number;
  adCoveragePct:    number;
  adsCreated:       number;
  adsReadyToExport: number;
  goodOpportunities: number | null;
  // Outcome split, added alongside the coverage fix. `adsCreated` counts
  // ATTEMPTS and always did — "12 ads were created" is true even when all 12
  // failed. What was missing is that nothing distinguished those 12 from 12
  // delivered creatives, so the tile read as a success count. Coverage itself
  // now counts only delivered.
  //
  // Optional: the backend PR must land first. Every read below is guarded so
  // this page renders unchanged against the older response shape.
  adsDelivered?:    number;
  adsFailed?:       number;
  adsInFlight?:     number;
  productsAttemptedNoneDelivered?: number;
};

type Pagination = {
  total:   number;
  offset:  number;
  limit:   number;
  hasMore: boolean;
};

type SummaryResponse = {
  summary:     Summary;
  products:    ProductRow[];
  pagination?: Pagination;
};

const PAGE_SIZE = 20;

type CategoryRow = {
  categoryId:   string;
  name:         string;
  breadcrumb:   string;
  depth:        number;
  url:          string | null;
  productCount: number;
  hasChildren:  boolean;
};

type ExpansionCampaign = {
  campaignId: string;
  name:       string;
  status:     string | null;
  kind:       string | null;
  adCount:    number;
};

export type RegenerationHistoryEntry = {
  prompt:      string;
  mode:        'light' | 'full';
  requestedBy: string | null;
  videoModel?: string | null;
  at:          string | null;
  status:      'pending' | 'done' | 'failed';
  error:       string | null;
  durationMs:  number | null;
};

// GET /api/ads/video-models row — the operator-selectable video models
// for the full-regen dropdown. Fetched once per page load (module cache).
type VideoModelOption = {
  slug: string;
  label: string;
  default: boolean;
  supportedAspectRatios: string[];
  requiresVideoSeed: boolean;
  estCostPer8s: number | null;
};
let videoModelsCache: VideoModelOption[] | null = null;

export type ExpansionAd = {
  adId:           string;
  productId?:     string | null;
  campaignId:     string | null;
  template:       string;
  aspectRatio:    string;
  platformFormat: string | null;
  kind:           'image' | 'video';
  // Intent profile (models/Ad.js funnelStage) — absent on most static ads
  // and on video ads that predate the funnel-stage retitle feature. Render
  // via funnelStageLabel(); absent must show as nothing, never a raw token.
  funnelStage?:   'awareness' | 'consideration' | 'conversion' | null;
  // THE canonical "is this ad a true video master" answer — computed
  // server-side (services/campaignAdsGenerationService.js isMasterVideoAd,
  // liquidretail_backend), the SAME four conditions
  // routes/ads.js findSiblingMasterAd requires of a candidate master
  // (kind==='video', no deriveFromMaster marker, no funnelStage,
  // videoDurationSec at/above Google's PMax floor). Never re-derive this
  // client-side from raw fields — a looser client-side approximation is
  // exactly how the two definitions would silently drift apart. Absent or
  // false on every non-video ad, every derivative/funnel-retitle, and any
  // ad minted before this field existed on the backend — never guessed
  // true. Drives the "Master" badge on AdThumbnail; NOT the same concept
  // as masterAdId below (this ad IS a master vs. this ad LINKS TO one).
  isMaster?:      boolean | null;
  // Retailer's own product-page link (CatalogProduct.productUrl), joined
  // + brand-scoped server-side (loadProductUrlMap — see PR #245 / #263).
  // null when there's no productId, an unlinked/soft-deleted product, or
  // a product with no URL on file. Never render a link when falsy.
  productUrl?:    string | null;
  sourceFileType: 'image' | 'video' | null;
  status:         string;
  // Same pipeline stamp the /ads gallery reads. Previously omitted
  // here, so AdThumbnail could only paint Queued / Rendering / Failed
  // even when the backend already knew the real step + queue depth.
  renderStage?:   string | null;
  renderStageAt?: string | null;
  approved:       boolean;
  approvedAt:     string | null;
  renderUrl:      string | null;
  photorealUrl:   string | null;
  useImageRefAsProduction: boolean;
  posterUrl:      string | null;
  headline:       string | null;
  ctaText:        string | null;
  generatedAt:    string | null;
  metaSyncStatus: string | null;
  metaAdId:       string | null;
  metaAdsetId:    string | null;
  regenerating:        boolean;
  regenerationStage:   string | null;
  regenerationHistory: RegenerationHistoryEntry[];
  // UGC-ads Phase 4 — populated by /api/catalog/:id/ads-detail so the
  // Product Ads expansion can badge variantKind='ugc' rows and deep-link
  // the badge to /ugc-ads?mediaId=<id>. sourceMedia is joined from the
  // Ad's source Media (fileUrl + fileType) so the UGC hover thumbnail
  // stays a single request, not a per-badge lazy load. null for non-UGC
  // ads and for UGC ads whose source Media was hard-deleted.
  variantKind:  string | null;
  mediaId:      string | null;
  sourceMedia:  { fileUrl: string | null; fileType: string | null } | null;
  // Downscaled Cloudinary grid-tile variants emitted by
  // /api/catalog/:id/ads-detail (same fields /api/ads returns) — ~480px for
  // video, ~640px for static images. Absent on older payloads — always
  // fall back to the full-resolution URL. See gridDisplayUrlFor() below;
  // AdDetailModal deliberately keeps using displayUrlFor() (full quality).
  previewVideoUrl?: string | null;
  previewImageUrl?: string | null;
  // Operator-facing failure headline — same field routes/ads.js projectAd
  // has always surfaced for a failed static ad; only ADDED to this
  // endpoint's allowlist now (routes/catalog.js's ads-detail $project) so
  // Product Ads stopped being the one surface where a failed ad's reason
  // silently arrived undefined. Present only when status === 'failed'.
  renderErrorMessage?: string | null;
  // Vision-QC verdict — services/adVisionQcService.js summarizeVisionQc,
  // the SAME formatter the flat /api/ads list and the run-level rollup use,
  // so "was this ad inspected, and why" never gets a second, drifting
  // derivation. null when the ad predates the QC gate or QC is disabled.
  visionQc?: {
    inspected:    boolean;
    passed:       boolean;
    skipped:      boolean;
    disabled:     boolean;
    reason:       string | null;
    finalAttempt: number | null;
    regenerated:  boolean;
    summary:      string | null;
    categories?: Record<string, { score: number | null; pass: boolean; findings: string[]; caveat?: string }>;
    // The EXACT text adVisionQcService.alertQcFailure already sent to
    // Slack for this verdict (buildQcSlackDetail's output) — present only
    // on a real failure (never on skipped/disabled/passed). This is what
    // "what was wrong with it" renders below: never a second, independent
    // description of the same verdict, so the app and Slack cannot drift.
    failureDetail?: string | null;
    qcAdvisoryOutline?: boolean;
    // Last vision-QC attempt's own candidate render URL — present only
    // on a genuine failure verdict. photorealUrl/renderUrl are often
    // both null on a QC-failed ad; displayUrlFor falls back to this so
    // the operator can see the rejected image while deciding whether
    // to vouch for it. Backend: summarizeVisionQc.
    discardedRenderUrl?: string | null;
  } | null;
  // Projected paint flag from summarizeVisionQc. Source of truth for the
  // red tile outline after QC-1. Absent on pre-QC-1 payloads — the SPA
  // falls back in shouldPaintQcOutline. Never fabricate true.
  qcAdvisoryOutline?: boolean;
  // THE canonical phase + failure classification — services/adPhase.js
  // deriveAdPhase / describeAdFailure on the backend, routes/catalog.js's
  // ads-detail $project already computes and returns both (same function
  // routes/ads.js projectAd and Slack's run-feed use), this type just
  // hadn't been updated to read them yet. `failure` is non-null on
  // failed-terminal, qc-failed-kept, AND qc-advisory (inspected fail
  // kept as a delivered draft). `failure.isQc` is what distinguishes an
  // automated vision-QC rejection from a hard render failure/timeout/
  // reclaimed-claim — the gate for the override control below (never
  // re-derive this from renderErrorMessage/visionQc directly). Do not
  // paint the outline from `phase`.
  phase?: string;
  failure?: {
    label:      string;
    isQc:       boolean;
    stage:      string | null;
    keptAsset:  boolean;
    qcCaveat:   string | null;
  } | null;
  // Operator override of a vision-QC rejection (POST /:id/override-qc) —
  // orthogonal to `approved`/`approvedAt` above. Persists even after the
  // ad later moves on (e.g. re-approved, re-exported), so this stays the
  // durable "a human vouched for this one" record. Optional: absent
  // reads as "never overridden", which is the correct default everywhere.
  // Every preview surface maps these through the shared toExpansionAd.
  qcOverridden?:       boolean;
  qcOverriddenAt?:     string | null;
  qcOverriddenBy?:     string | null;
  qcOverrideReason?:   string | null;
  // Master↔derivative link (requires a backend field not yet shipped — see
  // liquidretail_backend follow-up: expose Ad.deriveFromMaster resolved to
  // an actual sibling Ad via a duration-gate-free variant of
  // routes/ads.js findSiblingMasterAd). Absent on every ad today; when
  // present this ad IS a derivative of the referenced master.
  masterAdId?:           string | null;
  masterThumbnailUrl?:   string | null;
  masterStatus?:         string | null;
  masterPlatformFormat?: string | null;
  // Reverse direction — populated on a MASTER ad when it has known
  // derivatives, same backend follow-up as above. Absent today.
  derivativeCount?:      number | null;
  // Scene reel workflow (API contract §4). renderRoute is the Ad's own
  // render route ('veo' | 'html_gen' | 'scene_reel'); sceneReel is the
  // projected scene summary — null on every non-scene ad and on a backend
  // that predates the workflow. Read it through lib/sceneReel.ts.
  renderRoute?:          string | null;
  sceneReel?:            SceneReelSummary | null;
};

export function stageLabel(stage: string | null): string {
  switch (stage) {
    case 'pending':   return 'Queued…';
    case 'veo':       return 'Re-rolling video (~3 min)…';
    case 'chrome':    return 'Generating chrome…';
    case 'composite': return 'Compositing video…';
    case 'image-gen': return 'Regenerating layout…';
    case 'image-ref': return 'Polishing image…';
    // Single-scene regenerate on a scene reel (API contract §6). The ad
    // poller and this banner are shared with the whole-ad regenerate.
    case 'scene-frames': return 'Preparing new scene frames…';
    case 'scene-clip':   return 'Generating the new scene clip…';
    case 'scene-qc':     return 'Checking the new scene…';
    case 'reassemble':   return 'Re-cutting the 10 s reel…';
    case 'retitle':      return 'Re-titling…';
    case 'done':      return 'Done';
    case 'failed':    return 'Failed';
    default:          return 'Regenerating…';
  }
}

// Three-section grouping rule for the inline expansion. Mirrors the
// backend state model:
//   Draft    = not approved yet
//   Approved = operator-approved, not pushed to Meta yet
//   Exported = synced to Meta (metaSyncStatus='synced')
type AdSection = 'draft' | 'approved' | 'exported';
export function sectionFor(ad: ExpansionAd): AdSection {
  if (ad.metaSyncStatus === 'synced') return 'exported';
  if (ad.approved) return 'approved';
  return 'draft';
}

// Lets the operator flip between the grouped Draft/Approved/Exported view
// above and a single flat feed sorted newest-first (the order the backend
// already returns — see PRODUCT_ADS_FETCH_LIMIT's "most recent ads" note).
// Exported so Campaigns' identical expansion panel shares one default
// instead of each page hardcoding its own starting mode.
export type AdGroupMode = 'grouped' | 'flat';
export const DEFAULT_AD_GROUP_MODE: AdGroupMode = 'grouped';

// Same URL-priority rule as the legacy /ads page: video ads always use
// renderUrl (the ffmpeg composite); image ads ALWAYS prefer photorealUrl
// (the gpt-image-1 polished version) over renderUrl (the raw Puppeteer
// screenshot) whenever it's populated — regardless of the per-campaign
// useImageRefAsProduction flag, which is reserved for production-Meta
// pushes and is not the gate for the gallery thumbnail.
//
// QC-failed ads often have both of those null (the candidate was
// discarded rather than published). Fall back to
// visionQc.discardedRenderUrl — the last vision-QC attempt's own
// candidate, present only on a genuine failure verdict — so the
// operator can see what they are vouching for. Shared helper lives
// next to QcFailureReview so Ads / AdInspectCard / this page cannot
// drift.
export function displayUrlFor(ad: ExpansionAd): string | null {
  return qcReviewDisplayUrl(ad);
}

// Grid-tile source — same asset as displayUrlFor, but the backend's
// downscaled variant when it offered one (~480px for video, ~640px for
// static images), so this expansion grid doesn't stream N full-bitrate
// videos / N full-resolution PNGs (measured 1.5-4.3MB per PNG) for tiles a
// few hundred pixels wide. AdDetailModal deliberately keeps using
// displayUrlFor — full quality there. Falls back to displayUrlFor for
// older payloads with no preview*Url field, and whenever the backend
// couldn't transform the URL. Mirrors gridDisplayUrl() in pages/Ads.
export function gridDisplayUrlFor(ad: ExpansionAd): string | null {
  if (ad.kind === 'video') return ad.previewVideoUrl || displayUrlFor(ad);
  return ad.previewImageUrl || displayUrlFor(ad);
}

type ExpansionResponse = {
  campaigns: ExpansionCampaign[];
  ads:       ExpansionAd[];
};

// Meta AdSet shape returned by /api/ads/meta-adsets — same projection
// the legacy /ads page uses; only the fields we display here are typed.
type MetaAdset = {
  id:           string;
  name:         string;
  status:       string | null;
  campaignId:   string | null;
  campaignName: string | null;
};

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const t   = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.round((now - t) / 1000));
  if (sec < 60)        return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60)        return `${min}m ago`;
  const hr  = Math.round(min / 60);
  if (hr  < 24)        return `${hr}h ago`;
  const d   = Math.round(hr  / 24);
  return `${d}d ago`;
}

// Coverage now counts DELIVERED creatives, not bare Ad rows. That fixes the
// reported defect (12 failed ads reading as 100% covered) but it would have
// introduced a NEW untruth at the bottom of the scale if this function were
// left alone: a product whose 12 ads all failed lands on pct 0 and used to be
// labelled "No ads" — which is equally false, in the opposite direction, and
// would have hidden the failures completely.
//
// So the zero case is now split by OUTCOME. `attempted` distinguishes "nothing
// was ever tried" from "everything tried came back empty", and in-flight gets
// its own label because its remedy is "wait", not "regenerate".
function coverageLabel(
  pct: number,
  counts?: { attempted?: number; inFlight?: number }
): { label: string; color: string } {
  if (pct >= 80) return { label: 'Excellent', color: 'green' };
  if (pct >= 50) return { label: 'Good',      color: 'green' };
  if (pct >  0)  return { label: 'Low',       color: 'orange' };
  // pct === 0 — say WHY there is no coverage.
  if ((counts?.inFlight ?? 0) > 0)  return { label: 'Generating',        color: 'orange' };
  if ((counts?.attempted ?? 0) > 0) return { label: 'Nothing delivered', color: 'red'    };
  return { label: 'No ads', color: 'gray' };
}

function priceLabel(price: number | null, currency: string | null): string {
  if (price == null) return '';
  const sym = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '';
  return `${sym}${price.toFixed(2)}`;
}

export function ProductAdsPage() {
  const { activeBrand } = useBrand();
  const activeBrandId = activeBrand?.id ?? null;
  const navigate = useNavigate();
  const toast = useToast();

  const [summary, setSummary]   = useState<Summary | null>(null);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState<string | null>(null);

  // Category filter — cascading dropdowns. `categoryLevels[i]` is the
  // options list shown at level i; `categoryPath[i]` is the chosen id at
  // that level. Level 0 is top-level (parentId null); each subsequent
  // level lists the children of the chosen category above it. The
  // effective filter is the deepest id in categoryPath (empty = all
  // categories).
  const [categoryLevels, setCategoryLevels] = useState<CategoryRow[][]>([]);
  const [categoryPath, setCategoryPath]     = useState<string[]>([]);
  // A stale in-flight children-fetch (operator clicked a second time
  // before the first resolved) must not overwrite the current level;
  // we compare against the ref inside the resolver, not stale state.
  const categoryPathRef = useRef<string[]>([]);
  categoryPathRef.current = categoryPath;
  const selectedCategoryId = categoryPath.length > 0 ? categoryPath[categoryPath.length - 1] : 'all';

  // Inline expansion state — keyed by productId. Lazy load on first
  // expand; cached after that for the session.
  const [expandedId, setExpandedId]       = useState<string | null>(null);
  const [expansions, setExpansions]       = useState<Record<string, ExpansionResponse>>({});
  const [expansionLoading, setExpansionLoading] = useState<Record<string, boolean>>({});

  // Bulk selection — Set<productId>. Header checkbox flips the loaded
  // slice (not pagination.total). Cleared on brand/category change.
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Ad detail modal — single state across the whole page so any
  // expansion's thumbnail can open the same modal. Null = closed.
  const [openAd, setOpenAd] = useState<ExpansionAd | null>(null);
  // A copy-save or re-render can finish after the operator has already
  // closed the modal (or opened a different ad) — onMutated below guards
  // against a late completion silently reopening/overwriting the wrong ad.
  const openAdIdRef = useRef<string | null>(null);
  openAdIdRef.current = openAd?.adId ?? null;

  // Push the open ad's id into the agent's ambient context so an
  // operator asking "why did this ad fail?" while the detail modal
  // is up reaches ad.inspect(adId=…) without pasting an id. Cleared
  // automatically when the modal closes (undefined patch → hook
  // unregisters the key).
  useSetAgentContext({ adId: openAd?.adId });

  // Pagination — first page loads on brand/category change; subsequent
  // pages append on scroll via the sentinel at the bottom of the table.
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  // Set true synchronously at the start of loadMoreProducts — mirroring
  // `loadingMore` state is too late (two IntersectionObserver callbacks
  // in the same frame both see false and append the same page).
  const loadingMoreRef = useRef(false);
  const paginationRef = useRef<Pagination | null>(null);
  paginationRef.current = pagination;
  const selectedCategoryIdRef = useRef(selectedCategoryId);
  selectedCategoryIdRef.current = selectedCategoryId;
  const activeBrandIdRef = useRef(activeBrandId);
  activeBrandIdRef.current = activeBrandId;

  // Title search. `searchInput` is the raw controlled-input value;
  // `searchDebounced` is what actually hits the API (300ms) so keystrokes
  // don't fire a request per character. Backend filters at the Mongo
  // level, so pagination + KPI counts follow the search.
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  const searchDebouncedRef = useRef(searchDebounced);
  searchDebouncedRef.current = searchDebounced;

  // Bumped on every brand/category/search page-0 load so an in-flight
  // loadMore from the previous filter cannot append onto the new rows.
  const fetchGenRef = useRef(0);

  useEffect(() => {
    if (!activeBrandId) {
      fetchGenRef.current += 1;
      loadingMoreRef.current = false;
      setLoadingMore(false);
      setSelected(new Set());
      setProducts([]);
      setPagination(null);
      return;
    }
    const gen = ++fetchGenRef.current;
    let cancelled = false;
    loadingMoreRef.current = false;
    setLoadingMore(false);
    setSelected(new Set());
    setLoading(true);
    setErr(null);
    setProducts([]);
    setPagination(null);
    const params = new URLSearchParams({ brandId: activeBrandId });
    if (selectedCategoryId && selectedCategoryId !== 'all') params.set('categoryId', selectedCategoryId);
    if (searchDebounced) params.set('search', searchDebounced);
    params.set('limit',  String(PAGE_SIZE));
    params.set('offset', '0');
    apiJson<SummaryResponse>(`/api/catalog/ads-summary?${params.toString()}`)
      .then(res => {
        if (cancelled || gen !== fetchGenRef.current) return;
        setSummary(res.summary);
        setProducts(res.products || []);
        setPagination(res.pagination || null);
      })
      .catch(e => { if (!cancelled && gen === fetchGenRef.current) setErr(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled && gen === fetchGenRef.current) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeBrandId, selectedCategoryId, searchDebounced]);

  // Fetches the next page and appends. loadingMoreRef is set true
  // before the await so an IntersectionObserver double-fire (near the
  // boundary) cannot spawn duplicate requests. fetchGenRef + a re-read
  // of the selected category after the await drop a stale page when the
  // operator drills into a new filter mid-flight.
  async function loadMoreProducts() {
    const brandId = activeBrandIdRef.current;
    const paginationNow = paginationRef.current;
    const categoryId = selectedCategoryIdRef.current;
    const search = searchDebouncedRef.current;
    if (!brandId) return;
    if (loadingMoreRef.current) return;
    if (!paginationNow || !paginationNow.hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const gen = fetchGenRef.current;
    const nextOffset = paginationNow.offset + paginationNow.limit;
    const params = new URLSearchParams({ brandId });
    if (categoryId && categoryId !== 'all') params.set('categoryId', categoryId);
    if (search) params.set('search', search);
    params.set('limit',  String(PAGE_SIZE));
    params.set('offset', String(nextOffset));
    try {
      const res = await apiJson<SummaryResponse>(`/api/catalog/ads-summary?${params.toString()}`);
      if (gen !== fetchGenRef.current) return;
      if (selectedCategoryIdRef.current !== categoryId) return;
      if (searchDebouncedRef.current !== search) return;
      setProducts(prev => {
        const seen = new Set(prev.map(p => p.productId));
        const extra = (res.products || []).filter(p => !seen.has(p.productId));
        return extra.length ? [...prev, ...extra] : prev;
      });
      setPagination(res.pagination || null);
    } catch (e) {
      if (gen !== fetchGenRef.current) return;
      // A failed page-fetch shouldn't clobber the products already on
      // screen; surface the same error banner the initial load uses.
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      if (gen === fetchGenRef.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }

  // Auto-load on scroll: fire loadMoreProducts when the sentinel enters
  // the viewport, with an early rootMargin so the operator never sees a
  // hard stop at the bottom of the list.
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    if (!pagination || !pagination.hasMore) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) loadMoreProducts();
    }, { rootMargin: '400px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
    // loadMoreProducts is stable-enough for this pass (reads state via
    // refs/setState callback shape); intentionally depending on the
    // pagination pointer so the observer rebinds after each page lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination?.offset, pagination?.hasMore, activeBrandId, selectedCategoryId, searchDebounced]);

  // Top-level categories — loaded once per brand. Deeper levels are
  // loaded on demand as the operator drills down (see selectCategoryAt).
  useEffect(() => {
    if (!activeBrandId) {
      setCategoryLevels([]);
      setCategoryPath([]);
      return;
    }
    let cancelled = false;
    apiJson<CategoryRow[]>(`/api/catalog/categories?brandId=${encodeURIComponent(activeBrandId)}`)
      .then(rows => {
        if (cancelled) return;
        setCategoryLevels([rows || []]);
        setCategoryPath([]);
      })
      .catch(() => {
        if (cancelled) return;
        setCategoryLevels([]);
        setCategoryPath([]);
      });
    return () => { cancelled = true; };
  }, [activeBrandId]);

  // Pick a category at `levelIdx`. Truncates the path/levels below it,
  // then — if the chosen row has children — fetches the next level.
  // Value '' means "All at this level" and clears everything below.
  async function selectCategoryAt(levelIdx: number, categoryId: string) {
    if (!activeBrandId) return;
    if (!categoryId) {
      setCategoryPath(prev => prev.slice(0, levelIdx));
      setCategoryLevels(prev => prev.slice(0, levelIdx + 1));
      return;
    }
    const nextPath = [...categoryPath.slice(0, levelIdx), categoryId];
    setCategoryPath(nextPath);
    setCategoryLevels(prev => prev.slice(0, levelIdx + 1));

    const chosen = (categoryLevels[levelIdx] || []).find(r => r.categoryId === categoryId);
    if (!chosen || !chosen.hasChildren) return;

    const url = `/api/catalog/categories?brandId=${encodeURIComponent(activeBrandId)}&parentId=${encodeURIComponent(categoryId)}`;
    try {
      const children = await apiJson<CategoryRow[]>(url);
      if (categoryPathRef.current[levelIdx] !== categoryId) return;
      setCategoryLevels(prev => {
        if (prev.length <= levelIdx) return prev;
        const next = prev.slice(0, levelIdx + 1);
        next.push(children || []);
        return next;
      });
    } catch {
      // Silent — the parent's own productCount already told the operator
      // there are matching products; the parent stays selected as the filter.
    }
  }

  // Select-all covers the loaded slice only — ads-summary is paginated
  // (max 100, we request 20) and Generate Ads takes productIds on the
  // query string, so fetching the full id set for a 3,000-SKU brand is
  // both expensive and a wizard-sized problem. The header and bulk bar
  // say so when total > loaded.
  const loadedCount = products.length;
  const totalCount = pagination?.total ?? loadedCount;
  const allLoadedSelected = loadedCount > 0 && products.every(p => selected.has(p.productId));
  const someSelected = selected.size > 0 && !allLoadedSelected;

  function toggleAll() {
    if (allLoadedSelected) setSelected(new Set());
    else setSelected(new Set(products.map(p => p.productId)));
  }
  function toggleOne(productId: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }

  async function expandRow(productId: string) {
    const newId = expandedId === productId ? null : productId;
    setExpandedId(newId);
    if (newId && !expansions[productId] && activeBrandId) {
      setExpansionLoading(prev => ({ ...prev, [productId]: true }));
      try {
        const res = await apiJson<ExpansionResponse>(
          `/api/catalog/${encodeURIComponent(productId)}/ads-detail?brandId=${encodeURIComponent(activeBrandId)}`
        );
        setExpansions(prev => ({
          ...prev,
          [productId]: { ...res, ads: (res.ads || []).map(toExpansionAd) }
        }));
      } catch (e) {
        toast({
          title:       'Could not load product ads',
          description: e instanceof Error ? e.message : String(e),
          status:      'error',
          duration:    5000
        });
      } finally {
        setExpansionLoading(prev => ({ ...prev, [productId]: false }));
      }
    }
  }

  function generateForSelected() {
    if (selected.size === 0) return;
    const ids = Array.from(selected).join(',');
    navigate(`/generate-ads?productIds=${encodeURIComponent(ids)}`);
  }
  function generateForOne(productId: string) {
    navigate(`/generate-ads?productIds=${encodeURIComponent(productId)}`);
  }

  if (!activeBrandId) {
    return (
      <VStack align="stretch" spacing={4}>
        <PageHeader title="Product Ads" description="Generate and manage ads for your products at scale." />
        <Card variant="outline"><CardBody><Text fontSize="sm" color="brand.muted">Select a brand to continue.</Text></CardBody></Card>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <PageHeader title="Product Ads" description="Generate and manage ads for your products at scale." />

      {/* ── KPI tiles ─────────────────────────────────────────────── */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
        <KpiTile
          label="Ad Coverage"
          value={summary ? `${summary.adCoveragePct}%` : '—'}
          /* "N of M products" now means products with a DELIVERED creative.
             It read 2 of 200 for a product whose every ad had failed. When some
             product attempted and delivered nothing, say so here — otherwise
             the drop from "2 of 200" to "1 of 200" looks like data loss rather
             than the truth finally being reported. */
          sub={summary
            ? `${summary.productsWithAds} of ${summary.totalProducts} products delivered`
              + ((summary.productsAttemptedNoneDelivered ?? 0) > 0
                  ? ` · ${summary.productsAttemptedNoneDelivered} attempted, nothing delivered`
                  : '')
            : ''}
          progressPct={summary?.adCoveragePct ?? null}
          tooltip="Counts products with at least one deliverable creative (rendered and, for video, titled). Failed and in-flight ads are not coverage."
        />
        <KpiTile
          label="Good Opportunities"
          value={summary?.goodOpportunities ?? '—'}
          sub="Products with high opportunity scores"
          tooltip="Phase 2 — opportunity scoring engine"
        />
        {/* "Ads Created" counts ATTEMPTS, which is what the word means and is
            deliberately unchanged — the untruth was calling a product covered,
            not calling 12 ads created. But an undifferentiated count reads as a
            success count: this tile advanced 18 → 30 on a batch where all 12
            new ads failed with zero assets. The outcome split is now on the
            tile, so the number can no longer be mistaken for delivery. */}
        <KpiTile
          label="Ads Created"
          value={summary?.adsCreated ?? '—'}
          sub={summary && summary.adsDelivered !== undefined
            ? [
                `${summary.adsDelivered} delivered`,
                (summary.adsFailed ?? 0) > 0 ? `${summary.adsFailed} failed` : null,
                (summary.adsInFlight ?? 0) > 0 ? `${summary.adsInFlight} generating` : null
              ].filter(Boolean).join(' · ')
            : 'Total ads generated (attempts, not deliveries)'}
        />
        <KpiTile
          label="Ready to Export"
          value={summary?.adsReadyToExport ?? '—'}
          sub="Ads ready to push"
        />
      </SimpleGrid>

      {/* ── Search bar ─────────────────────────────────────────────── */}
      <HStack spacing={3} align="center" flexWrap="wrap">
        <Text fontSize="xs" color="brand.muted" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em">
          Search
        </Text>
        <Input
          size="sm"
          maxW="360px"
          placeholder="Search products by title…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {searchDebounced && (
          <Button size="xs" variant="ghost" onClick={() => setSearchInput('')}>
            Clear
          </Button>
        )}
      </HStack>

      {/* ── Filter bar — cascading category dropdowns ──────────────── */}
      {categoryLevels[0] && categoryLevels[0].length > 0 && (
        <HStack spacing={3} align="center" flexWrap="wrap">
          <Text fontSize="xs" color="brand.muted" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em">
            Category
          </Text>
          {categoryLevels.map((rows, levelIdx) => {
            if (!rows || rows.length === 0) return null;
            const value = categoryPath[levelIdx] || '';
            const allLabel = levelIdx === 0
              ? `All categories (${rows.reduce((s, c) => s + c.productCount, 0)})`
              : 'All';
            return (
              <Select
                key={levelIdx}
                size="sm"
                maxW="260px"
                value={value}
                onChange={(e) => selectCategoryAt(levelIdx, e.target.value)}
              >
                <option value="">{allLabel}</option>
                {rows.map(c => (
                  <option key={c.categoryId} value={c.categoryId}>
                    {c.name} ({c.productCount})
                  </option>
                ))}
              </Select>
            );
          })}
        </HStack>
      )}

      {/* ── Bulk selection bar ─────────────────────────────────────── */}
      {selected.size > 0 && (
        <Card variant="outline" borderColor="rsViolet.400" bg="rsViolet.50">
          <CardBody py={2} px={4}>
            <HStack spacing={4} align="center">
              <Text fontSize="sm" fontWeight="700" color="brand.ink">
                {selected.size} product{selected.size === 1 ? '' : 's'} selected
                {allLoadedSelected && totalCount > loadedCount
                  ? ` (${loadedCount} of ${totalCount} loaded)`
                  : ''}
              </Text>
              <Button size="sm" variant="brand" onClick={generateForSelected}>
                Generate Ads
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                Clear selection
              </Button>
            </HStack>
          </CardBody>
        </Card>
      )}

      {/* ── Product table ─────────────────────────────────────────── */}
      {loading && (
        <Card variant="outline"><CardBody><HStack py={6} justify="center"><Spinner size="sm" /><Text fontSize="sm" color="brand.muted">Loading products…</Text></HStack></CardBody></Card>
      )}
      {err && !loading && (
        <Card variant="outline"><CardBody><Text fontSize="sm" color="red.600">{err}</Text></CardBody></Card>
      )}
      {!loading && !err && products.length === 0 && (
        <Card variant="outline">
          <CardBody>
            <VStack align="stretch" spacing={3} py={6} textAlign="center">
              <Text fontSize="sm" color="brand.muted" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em">
                No products yet
              </Text>
              <Text color="brand.ink" fontWeight="600" fontSize="md">
                Sync a catalog source or upload products to start generating ads.
              </Text>
              <HStack justify="center" spacing={2} pt={2}>
                <Button as={RouterLink} to="/brand" variant="outline" size="sm">Connect catalog</Button>
                <Button as={RouterLink} to="/catalog" variant="brand" size="sm">Browse catalog</Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      )}

      {!loading && !err && products.length > 0 && (
        <Card variant="outline">
          <CardBody p={0}>
            {/* Header row */}
            <HStack
              px={4}
              py={3}
              spacing={4}
              fontSize="11px"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing="0.06em"
              color="brand.muted"
              borderBottomWidth="1px"
              borderColor="brand.border"
              align="center"
            >
              <Checkbox
                isChecked={allLoadedSelected}
                isIndeterminate={someSelected}
                onChange={toggleAll}
                aria-label={totalCount > loadedCount
                  ? `Select all ${loadedCount} loaded products of ${totalCount}`
                  : 'Select all products'}
                title={totalCount > loadedCount
                  ? `Selects the ${loadedCount} loaded products, not all ${totalCount}`
                  : undefined}
              />
              <Box flex="2 1 0">Product</Box>
              <Box flex="1 1 0">Ad Coverage</Box>
              <Box flex="1 1 0" textAlign="center">Campaigns</Box>
              <Box flex="1 1 0" textAlign="center">Ads</Box>
              <Box flex="1 1 0">Last Activity</Box>
              <Box flex="1.4 1 0" textAlign="right">Actions</Box>
            </HStack>

            {/* Body rows */}
            <VStack align="stretch" spacing={0} divider={<Box borderTopWidth="1px" borderColor="brand.border" />}>
              {products.map(p => (
                <ProductRowView
                  key={p.productId}
                  row={p}
                  expanded={expandedId === p.productId}
                  expansion={expansions[p.productId] || null}
                  expansionLoading={!!expansionLoading[p.productId]}
                  selected={selected.has(p.productId)}
                  onToggleSelect={() => toggleOne(p.productId)}
                  onExpand={() => expandRow(p.productId)}
                  onGenerate={() => generateForOne(p.productId)}
                  onOpenAd={setOpenAd}
                />
              ))}
            </VStack>
            {/* Sentinel — IntersectionObserver fires loadMoreProducts
                when this scrolls near the viewport (rootMargin 400px). */}
            {pagination && pagination.hasMore && (
              <Box ref={loadMoreRef} py={4}>
                <HStack justify="center" spacing={2}>
                  {loadingMore ? (
                    <>
                      <Spinner size="sm" />
                      <Text fontSize="xs" color="brand.muted">Loading more…</Text>
                    </>
                  ) : (
                    <Text fontSize="xs" color="brand.muted">
                      {products.length} of {pagination.total} products
                    </Text>
                  )}
                </HStack>
              </Box>
            )}
          </CardBody>
        </Card>
      )}

      {/* Ad detail modal — opens on thumbnail click. Hosts the larger
          preview + Approve + Push-to-Meta (Export) actions. On a
          state change (approve toggled, export pushed) the open
          product's expansion is refetched so counts + sections stay
          in sync. */}
      {openAd && activeBrandId && (
        <AdDetailModal
          ad={openAd}
          brandId={activeBrandId}
          onClose={() => setOpenAd(null)}
          onOpenMaster={(masterAdId) => {
            // Derivative + master share productId, so the master is
            // usually already in this product's fetched expansion.
            const productId = openAd.productId || expandedId;
            const fromAdId = openAd.adId;
            void resolveAndOpenMaster({
              masterAdId,
              brandId: activeBrandId,
              siblingAds: productId ? expansions[productId]?.ads ?? null : null,
              isCurrent: () => openAdIdRef.current === fromAdId,
              setOpenAd,
              toast
            });
          }}
          onMutated={async (updated) => {
            // A copy-save or re-render kicked off before close/switch can
            // resolve after — don't let it reopen a closed modal or
            // overwrite a since-opened different ad.
            if (openAdIdRef.current !== updated.adId) return;
            // Patch the open ad inline + refresh expansion for the
            // product so section grouping rebalances.
            setOpenAd(updated);
            // Find which product's expansion contains this ad and
            // invalidate its cache so the next open re-fetches.
            for (const [productId, exp] of Object.entries(expansions)) {
              if (exp.ads.some(a => a.adId === updated.adId)) {
                const res = await apiJson<ExpansionResponse>(
                  `/api/catalog/${encodeURIComponent(productId)}/ads-detail?brandId=${encodeURIComponent(activeBrandId)}`
                ).catch(() => null);
                if (res) {
                  setExpansions(prev => ({
                    ...prev,
                    [productId]: { ...res, ads: (res.ads || []).map(toExpansionAd) }
                  }));
                }
                break;
              }
            }
          }}
        />
      )}
    </VStack>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────

// Ad detail modal — full-size preview + Approve + Push-to-Meta. Push
// uses the same /api/ads/push-to-meta endpoint the legacy /ads page
// drives. The response's per-ad list is `perAd` (not `results`); see
// metaPush.ts. Adset list is fetched lazily on first export click so the
// modal stays responsive when just viewing.
// Shape returned by GET /api/ads/:id is projectAd's full shape —
// overlaps with ExpansionAd on the fields the modal cares about
// but uses different names (id vs adId, copy.headline vs headline,
// etc.). Map the subset we need. Backend projections return
// ctaText with copy.cta_text-first fallback; headline lives on copy.
type PolledAdRow = {
  id:                  string;
  renderUrl:           string | null;
  photorealUrl:        string | null;
  posterUrl:           string | null;
  approved:            boolean;
  approvedAt:          string | null;
  metaSyncStatus:      string | null;
  metaAdId:            string | null;
  metaAdsetId:         string | null;
  regenerating:        boolean;
  regenerationStage:   string | null;
  regenerationHistory: RegenerationHistoryEntry[];
  renderStage?:        string | null;
  renderStageAt?:      string | null;
  // Scene reel (API contract §4) — absent on every other ad.
  status?:             string;
  renderRoute?:        string | null;
  sceneReel?:          unknown;
  headline?:           string | null;
  ctaText?:            string | null;
  copy?: {
    headline?: string | null;
    cta_text?: string | null;
  };
};

// AdDetailModal's regenerate poll merge. Whitelist merge — do NOT spread the
// frozen `ad` closure for copy fields or they revert to pre-poll stale values
// every 5s.
function mergePolledAdRow(ad: ExpansionAd, r: PolledAdRow): ExpansionAd {
  return {
    ...ad,
    renderUrl:           r.renderUrl,
    photorealUrl:        r.photorealUrl,
    posterUrl:           r.posterUrl,
    approved:            r.approved,
    approvedAt:          r.approvedAt,
    metaSyncStatus:      r.metaSyncStatus,
    metaAdId:            r.metaAdId,
    metaAdsetId:         r.metaAdsetId,
    regenerating:        r.regenerating,
    regenerationStage:   r.regenerationStage,
    regenerationHistory: r.regenerationHistory || [],
    renderStage:         r.renderStage !== undefined ? r.renderStage : ad.renderStage,
    renderStageAt:       r.renderStageAt !== undefined ? r.renderStageAt : ad.renderStageAt,
    headline:            r.copy?.headline ?? r.headline ?? ad.headline,
    ctaText:             r.copy?.cta_text ?? r.ctaText ?? ad.ctaText,
    // Scene reel fields ride along only when the row IS a scene reel, so
    // a non-scene ad merges exactly the fields above and nothing else —
    // even if a projection sends `sceneReel: null` on it rather than
    // omitting the key. A scene regenerate is how a failed reel
    // completes, so its status (failed → draft) and scene summary must
    // refresh with the poll; a row without a summary keeps the one shown.
    ...(isSceneReelAd({ renderRoute: r.renderRoute, sceneReel: r.sceneReel }) ? {
      renderRoute: r.renderRoute ?? ad.renderRoute ?? null,
      sceneReel:   toSceneReelSummary(r.sceneReel) ?? ad.sceneReel ?? null,
      ...(typeof r.status === 'string' ? { status: r.status } : {})
    } : {})
  };
}

export function AdDetailModal({
  ad, brandId, onClose, onMutated, onOpenMaster
}: {
  ad:           ExpansionAd;
  brandId:      string;
  onClose:      () => void;
  onMutated:    (updated: ExpansionAd) => void;
  onOpenMaster: (masterAdId: string) => void;
}) {
  const toast = useToast();

  // Placement chrome for the large preview.
  const chromeRatio = normalizeRatio(ad.aspectRatio);
  const chromeIdentity = useChromeIdentity({ ctaText: ad.ctaText });
  const {
    chrome: previewChrome,
    setChrome: setPreviewChrome,
    options: chromeOptions
  } = useAdChrome(chromeRatio, ad.platformFormat);
  const productHref = safeHttpUrl(ad.productUrl);

  const [showInspector, setShowInspector] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushNotice, setPushNotice] = useState<{ tone: MetaPushTone; text: string } | null>(null);
  const [adsets, setAdsets] = useState<MetaAdset[] | null>(null);
  const [adsetsLoading, setAdsetsLoading] = useState(false);
  const [chosenAdsetId, setChosenAdsetId] = useState<string>('');

  // QC-override UI state lives in <QcFailureReview> — do not re-own it
  // here (a leftover open panel from ad A used to be able to submit
  // against ad B; the shared component resets on adId itself).

  // Regenerate UI state
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenPrompt, setRegenPrompt] = useState('');
  const [regenModel, setRegenModel] = useState<string>('');   // '' = brand default
  const [videoModels, setVideoModels] = useState<VideoModelOption[] | null>(videoModelsCache);
  const [regenSubmitting, setRegenSubmitting] = useState(false);
  // 720p quality-repair regenerate — video only. Same POST /regenerate
  // as submitRegen, body { videoResolutionOverride: '720p' }, no prompt.
  // Open/submitting pair mirrors the regenerate panel (no reason textarea:
  // this is a paid re-render, not an audit-trail override).
  const [regen720Open, setRegen720Open] = useState(false);
  const [regen720Submitting, setRegen720Submitting] = useState(false);
  // Advanced — raw camera prompt (video full re-roll only). Mirrors the
  // wizard's "ADVANCED — RAW PROMPT" disclosure: load the canonical
  // scaffold, edit it, send as videoPromptRaw so this re-roll REPLACES
  // the camera prompt instead of prepending a refinement. Not persisted
  // on the Ad — next regenerate without a raw field reverts to canonical
  // (or any wizard-stamped videoPromptRaw still on the row).
  const [regenRaw, setRegenRaw] = useState('');
  const [scaffoldLoading, setScaffoldLoading] = useState(false);
  const [scaffoldMeta, setScaffoldMeta] = useState<{ model: string; byteCap: number } | null>(null);
  // Advanced — raw IMAGE prompt (static ads). Mirrors the video block below:
  // load the exact prompt that produced the current render, edit it, and send
  // it as imagePromptRaw so this regenerate REPLACES the whole image prompt
  // instead of appending a refinement note. Pass-through — never persisted on
  // the Ad, so the next regenerate with an empty box reverts to the
  // auto-composed prompt.
  const [imgRaw, setImgRaw] = useState('');
  const [imgPromptLoading, setImgPromptLoading] = useState(false);
  const [imgPromptMeta, setImgPromptMeta] = useState<{ model: string; size: string | null } | null>(null);

  // Per-ad title copy override (headline / CTA) + optional title re-render.
  const [editHeadline, setEditHeadline] = useState(ad.headline || '');
  const [editCta, setEditCta] = useState(ad.ctaText || '');
  const [copySaving, setCopySaving] = useState(false);
  const [titleRerendering, setTitleRerendering] = useState(false);
  // Cache-bust token so the <video> reloads when renderUrl is rewritten in place.
  const [renderBust, setRenderBust] = useState(0);
  // Last-seen prop copy — used so prop-sync only overwrites drafts when the
  // operator has not made local edits relative to the previous server values.
  const prevCopyPropsRef = useRef<{ headline: string | null; ctaText: string | null }>({
    headline: ad.headline,
    ctaText:  ad.ctaText
  });
  // Live open-ad id for stale-response guards (async handlers close over a
  // frozen `ad`; this ref always tracks the currently-open row).
  const openAdIdRef = useRef(ad.adId);
  openAdIdRef.current = ad.adId;
  const qcDetailFetchedFor = useRef<string | null>(null);

  // Lazy-load the selectable video models the first time the regen
  // panel opens for a video ad; cached module-wide for the session.
  // On failure the state stays null (NOT []) so the next panel open
  // retries instead of pinning "Brand default only" for the session.
  useEffect(() => {
    if (!regenOpen || ad.kind !== 'video' || videoModels != null) return;
    let cancelled = false;
    apiJson<{ models: VideoModelOption[] }>('/api/ads/video-models')
      .then(r => {
        videoModelsCache = r.models || [];
        if (!cancelled) setVideoModels(videoModelsCache);
      })
      .catch(() => { /* leave null — retried on next open */ });
    return () => { cancelled = true; };
  }, [regenOpen, ad.kind, videoModels]);

  // A per-run model pick must not outlive its context: reset when the
  // panel closes or the modal instance moves to a different ad —
  // otherwise a leftover slug from ad A silently rides into ad B's
  // (or the next session's) full re-roll POST and cost hint. Same for
  // the Advanced raw-prompt draft — a leftover raw from ad A must never
  // replace ad B's camera prompt by accident.
  useEffect(() => {
    if (!regenOpen) {
      setRegenModel('');
      setRegenRaw('');
      setScaffoldMeta(null);
      setImgRaw('');
      setImgPromptMeta(null);
    }
  }, [regenOpen]);
  useEffect(() => {
    setRegenModel('');
    setRegenRaw('');
    setScaffoldMeta(null);
    setImgRaw('');
    setImgPromptMeta(null);
    // Also close the regenerate panel/prompt and the inspector — added
    // alongside the master↔derivative link, whose in-place open swaps this
    // SAME modal instance to a different `ad` without unmounting it. Without
    // this, opening a master from a derivative (or vice versa) could leave
    // the old ad's regen panel open with its old refinement text still in
    // the box, now aimed at the new ad.
    setRegenOpen(false);
    setRegenPrompt('');
    setShowInspector(false);
    // MONEY / CAMPAIGN-CORRECTNESS — adversarial finding: the reset above
    // covered regen/inspector but not the Export→AdSet→Push flow's own
    // state. Without this, opening a master from a derivative (or vice
    // versa) mid-export left `adsets`/`chosenAdsetId` populated from the
    // PREVIOUS ad — e.g. approve+export+pick-an-AdSet on a derivative, then
    // open its master: the AdSet picker stayed open with the SAME AdSet
    // selected, and if the master happened to already be approved, "Push to
    // Meta" was one click from pushing the MASTER creative into the
    // DERIVATIVE's chosen AdSet. Full reset on every ad switch, same as the
    // regen state above.
    setApproving(false);
    setPushing(false);
    setAdsets(null);
    setAdsetsLoading(false);
    setChosenAdsetId('');
    setPushNotice(null);
    // QC-override panel reset lives in <QcFailureReview> (it keys on ad.adId).
    // Categories-fetch guard must still reset here so a newly opened ad
    // is not skipped because the previous row already fetched.
    qcDetailFetchedFor.current = null;
    // 720p panel — a leftover open/submitting state from ad A must never
    // fire a billable regenerate against ad B (this modal instance is reused
    // across ads without unmounting).
    setRegen720Open(false);
    setRegen720Submitting(false);
  }, [ad.adId]);

  // Reset draft only when switching ads. Keep as-is (full reset).
  useEffect(() => {
    setEditHeadline(ad.headline || '');
    setEditCta(ad.ctaText || '');
    setRenderBust(0);
    prevCopyPropsRef.current = { headline: ad.headline, ctaText: ad.ctaText };
  }, [ad.adId]);

  // Prop-sync: copy server values into drafts only when there are no
  // unsaved local edits (draft still equals the previous prop values).
  // Do NOT compare against current props — that would treat a legitimate
  // external prop update as "dirty" and skip the sync forever.
  useEffect(() => {
    const prev = prevCopyPropsRef.current;
    const noLocalEdits =
      editHeadline.trim() === (prev.headline || '').trim() &&
      editCta.trim() === (prev.ctaText || '').trim();
    if (noLocalEdits) {
      setEditHeadline(ad.headline || '');
      setEditCta(ad.ctaText || '');
    }
    prevCopyPropsRef.current = { headline: ad.headline, ctaText: ad.ctaText };
    // Intentionally omit editHeadline/editCta from deps: we only evaluate
    // local dirtiness at the moment server props change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ad.headline, ad.ctaText]);

  const url = displayUrlFor(ad);
  const videoSrc = url && renderBust
    ? `${url}${url.includes('?') ? '&' : '?'}_cb=${renderBust}`
    : (url || undefined);
  const copyDirty =
    (editHeadline.trim() !== (ad.headline || '').trim()) ||
    (editCta.trim() !== (ad.ctaText || '').trim());
  const isExported = ad.metaSyncStatus === 'synced';
  const isApproved = ad.approved;
  // Same renderStage→label table AdThumbnail's pipeline pill reads, reused
  // here so the modal never disagrees with the card that opened it. An ad
  // tile is clickable (and this modal openable) the instant it's minted —
  // see CampaignDetail/ProductAds/UgcAds, all unconditional onClick — so
  // every status below, not just 'draft', can land here.
  const pipelineStatusBadge = resolveAdStatusBadge(
    ad.status,
    ad.kind,
    ad.renderStage ?? null,
    ad.renderStageAt
  );
  // Header workflow badge. isExported/isApproved outrank pipeline state on
  // purpose (an exported or operator-approved ad reads that way regardless
  // of a stale renderStage). Below that, each status gets an explicit word:
  // 'draft' is the delivered state (routes/ads.js money-guard comment) but
  // is stamped BEFORE titling starts, so a draft ad still moving
  // (pipelineStatusBadge.inProgress) is not finished yet and must show the
  // real step, not "Ready for Review" — and 'queued' with no stage yet is
  // "never started", never to be confused with an in-progress pill.
  const qcOutline = shouldPaintQcOutline(ad);
  const showQcFindings = qcOutline || (!!ad.failure?.isQc && !ad.qcOverridden);
  const workflowBadge: { text: string; colorScheme: string; title?: string } =
    isExported  ? { text: 'Exported', colorScheme: 'blue' } :
    isApproved  ? { text: 'Approved', colorScheme: 'green' } :
    qcOutline                ? { text: ad.failure?.label || 'QC Fail', colorScheme: 'red' } :
    ad.status === 'failed'   ? { text: 'Failed',   colorScheme: 'orange' } :
    ad.status === 'archived' ? { text: 'Archived', colorScheme: 'gray' } :
    ad.status === 'queued'   ? { text: 'Queued',   colorScheme: 'gray' } :
    ad.status === 'live'     ? { text: 'Live',     colorScheme: 'green' } :
    pipelineStatusBadge.inProgress
      ? {
          text: pipelineStatusBadge.detail
            ? `${pipelineStatusBadge.label} · ${pipelineStatusBadge.detail}`
            : pipelineStatusBadge.label,
          colorScheme: adStatusBadgeColorScheme(pipelineStatusBadge.tone),
          title: pipelineStatusBadge.detail || undefined,
        }
    : ad.status === 'rendering' ? { text: 'Rendering…', colorScheme: 'gray' }
    : { text: 'Ready for Review', colorScheme: 'purple' }; // status === 'draft', terminal
  const isRegenerating = !!ad.regenerating;
  // Scene reels regenerate one scene at a time (SceneStrip). The whole-ad
  // Regenerate / 720p controls are hidden for them: the backend refuses them
  // (409 SCENE_REEL_REGENERATE_PER_SCENE), and a whole-ad re-roll would buy a
  // full video on the wrong model. Retitle and copy edits stay.
  const sceneReelAd = isSceneReelAd(ad);
  const lastHistory = (ad.regenerationHistory && ad.regenerationHistory.length)
    ? ad.regenerationHistory[ad.regenerationHistory.length - 1]
    : null;
  const lastError = lastHistory?.status === 'failed' ? (lastHistory.error || null) : null;

  // Compact list payloads (Campaign Detail, UGC Ads, agent ResourceCard)
  // omit visionQc.categories. Product Ads ads-detail already has them.
  // Fetch the full projectAd once on open when we need to paint QC
  // findings and scores are missing. Do not ask the list endpoint to
  // carry categories on every row.
  const needsQcCategories = showQcFindings && !ad.visionQc?.categories;
  useEffect(() => {
    if (!needsQcCategories) return;
    if (qcDetailFetchedFor.current === ad.adId) return;
    qcDetailFetchedFor.current = ad.adId;
    const startedAdId = ad.adId;
    let cancelled = false;
    apiJson<{ ad: { visionQc?: ExpansionAd['visionQc']; qcAdvisoryOutline?: boolean; failure?: ExpansionAd['failure'] } }>(
      `/api/ads/${encodeURIComponent(startedAdId)}?brandId=${encodeURIComponent(brandId)}`
    )
      .then((res) => {
        if (cancelled || openAdIdRef.current !== startedAdId) return;
        if (!res.ad?.visionQc) return;
        onMutated({
          ...ad,
          visionQc: res.ad.visionQc,
          ...(typeof res.ad.qcAdvisoryOutline === 'boolean'
            ? { qcAdvisoryOutline: res.ad.qcAdvisoryOutline }
            : {}),
          ...(res.ad.failure ? { failure: res.ad.failure } : {})
        });
      })
      .catch(() => { /* compact summary still renders */ });
    return () => { cancelled = true; };
    // `ad` / `onMutated` identities change every parent render; key on
    // the row + whether scores are still missing so this fires once.
  }, [ad.adId, brandId, needsQcCategories]);

  // Poll the ad's detail row every 5s while regenerating. Stops when
  // regenerating flips false. Uses the parent product's ads-detail
  // endpoint (one query for the whole product, then we pick out our
  // ad) — saves adding a per-ad endpoint just for polling.
  //
  // A scene reel keeps polling past that while its retitle is pending
  // (sceneReel.retitlePending, contract §6): a scene regenerate settles when
  // its new plate lands and stamps the retitle in the same write, and only
  // that retitle writes the titled renderUrl — and a failed reel's move to
  // draft. On every other ad `pollBusy` is exactly `isRegenerating`.
  //
  // That retitle follow is bounded (RETITLE_FOLLOW_MAX_MS): a retitle that
  // never settles stops the poll, and the strip says it is still re-titling
  // and to refresh later. Only the retitle-only phase is bounded; while
  // `regenerating` the poll runs as it always has.
  const pollBusy = sceneReelBusy(ad);
  // The ad whose retitle follow ran out; the note shows only while that ad
  // is still waiting on its retitle.
  const [retitleFollowStoppedFor, setRetitleFollowStoppedFor] = useState<string | null>(null);
  const retitleFollowStopped = retitleFollowStoppedFor === ad.adId && retitleOnlyPending(ad);
  useEffect(() => {
    if (!pollBusy || !ad.campaignId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // A follow (re)starting is a fresh bound.
    setRetitleFollowStoppedFor(null);
    // When this follow first saw only the retitle left; null while a
    // regenerate runs (and always on a non-scene ad).
    let retitleSince: number | null = retitleOnlyPending(ad) ? Date.now() : null;

    const schedule = (ms: number) => {
      if (retitleSince != null && Date.now() - retitleSince >= RETITLE_FOLLOW_MAX_MS) {
        setRetitleFollowStoppedFor(ad.adId);
        return;
      }
      timer = setTimeout(tick, ms);
    };

    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await apiJson<{ ad: PolledAdRow }>(
          `/api/ads/${encodeURIComponent(ad.adId)}?brandId=${encodeURIComponent(brandId)}`
        );
        if (cancelled) return;
        const r = res.ad;
        onMutated(mergePolledAdRow(ad, r));
        if (sceneReelBusy(r)) {
          retitleSince = retitleOnlyPending(r) ? (retitleSince ?? Date.now()) : null;
          schedule(5000);
        }
      } catch {
        // Network error — back off and try again. Stops only on cancel, or
        // when a retitle-only follow runs out.
        if (!cancelled) schedule(10000);
      }
    };
    timer = setTimeout(tick, 5000);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  // Only re-arm when the ad turns busy. ad reference changes on every
  // mutation but we don't want to restart the poll on every tick — and a
  // settle that leaves a retitle pending keeps pollBusy true, so the tick
  // loop above carries on without a restart.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollBusy, ad.adId, brandId]);

  // Submit is legal on any ad that is not a derivative — INCLUDING with every
  // text field empty. An empty regenerate used to be blocked here, mirroring a
  // backend 400; both are gone (see canSubmitRegen below). The refinement and
  // raw fields steer the re-render when supplied; none of them is required.
  const regenRawTrimmed = regenRaw.trim();
  const regenPromptTrimmed = regenPrompt.trim();
  // EVERY video regenerate re-rolls the master — there is no cheaper mode.
  // The old `regenMode === 'full'` half of this condition came from a
  // chrome-only option that the backend deleted in a23801e7 but the UI kept
  // advertising: leaving the box unchecked posted mode:'light', the API
  // echoed mode:'light' back, and adRegenerateService still billed a full
  // Omni master. Worse, an unchecked box ALSO dropped videoModel and
  // videoPromptRaw from the request, so the operator paid for a re-roll and
  // was denied the controls that steer it. Both halves are fixed here.
  const videoFullReroll = ad.kind === 'video';
  // Static raw prompt — no light/full split on the image path, so it needs no
  // re-roll gate. Raw alone is enough intent (the API allows an empty
  // refinement when imagePromptRaw is set).
  const imgRawTrimmed = ad.kind !== 'video' ? imgRaw.trim() : '';
  // A PLAIN RE-RENDER WITH NO REFINEMENT IS ALLOWED, and is the natural
  // default. Owner directive 2026-08-26, verbatim: "I should be able to
  // regenerate with no operator refinement."
  //
  // This used to additionally require at least one of refinement / raw camera
  // prompt / raw image prompt, mirroring a backend 400 that no longer exists.
  // Submitting nothing now re-runs the ad against the byte-identical untouched
  // pipeline prompt (verified: buildVeoPrompt with an empty operator prompt
  // emits no OPERATOR REFINEMENT block, so the assembled text is unchanged) —
  // which is exactly what "regenerate this as-is" should mean.
  //
  // ⚠️ THE NON-EMPTINESS CLAUSE IS THE ONLY ONE REMOVED. Every other reason a
  // regenerate must not be submitted is kept, and two are now enforced HERE
  // that previously were not:
  //   • masterAdId — a derivative (Meta crop, funnel retitle, PMax square) has
  //     no generation of its own and adgen's regenerateConsumer refuses ANY
  //     regenerate on it, so the button would just 409.
  //   • isExported / isRegenerating — these gate the footer toggle that OPENS
  //     this panel, which was sufficient only while an empty box also blocked
  //     submit. The panel stays mounted while the modal polls, so an export or
  //     a regen starting in another tab could flip either flag with the panel
  //     already open and the button live. Folding them in closes that window
  //     rather than relying on the server's 409 to explain it after the fact.
  //
  // ⚠️ DO NOT re-add an emptiness requirement, and do not substitute a default
  // prompt string to keep the button live — a synthesised refinement would be
  // injected as an OPERATOR REFINEMENT block and the re-render would no longer
  // reproduce the original prompt.
  const canSubmitRegen = !ad.masterAdId && !isExported && !isRegenerating;
  // Whether this submit carries any operator text. Not a gate any more — it
  // drives the button label and helper copy, so a refinement-free submit reads
  // as a deliberate choice rather than an unfinished form.
  const regenHasOperatorText = !!regenPromptTrimmed
    || (videoFullReroll && !!regenRawTrimmed)
    || !!imgRawTrimmed;
  // Server-side the raw override wins and the refinement note is dropped —
  // dim/disable the refinement box for both video raw and static raw.
  const rawSuppressesRefinement = (videoFullReroll && !!regenRawTrimmed) || !!imgRawTrimmed;

  const loadRegenScaffold = async () => {
    if (!ad.campaignId) {
      toast({
        title: 'No campaign on this ad',
        description: 'The scaffold needs a campaign so it can resolve brand + format context.',
        status: 'warning',
        duration: 4000
      });
      return;
    }
    setScaffoldLoading(true);
    try {
      // Same call shape as the wizard's Advanced — raw prompt loader
      // (GenerateAds VideoControlsStrip). durationSec omitted: the
      // scaffold defaults server-side; ExpansionAd does not carry
      // videoDurationSec today.
      const qp = new URLSearchParams({ campaignId: ad.campaignId });
      if (ad.productId) qp.set('productId', ad.productId);
      if (ad.platformFormat) qp.set('platformFormat', ad.platformFormat);
      const resp = await apiJson<{ prompt: string; model: string; byteCap: number }>(
        `/api/ads/veo-prompt-scaffold?${qp.toString()}`
      );
      setRegenRaw(resp.prompt || '');
      setScaffoldMeta({ model: resp.model, byteCap: resp.byteCap });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'unknown error';
      toast({ title: 'Scaffold load failed', description: msg, status: 'error', duration: 6000 });
    } finally {
      setScaffoldLoading(false);
    }
  };

  // The exact prompt that produced the render on screen — read from the
  // ad's recorded imageGeneration capture (written at submit time on every
  // render, including regenerates). This is deliberately NOT a re-derived
  // scaffold: it is what actually ran, so editing it is a true A/B.
  const loadCurrentImagePrompt = async () => {
    setImgPromptLoading(true);
    try {
      const resp = await apiJson<{ inspector: InspectorData }>(
        `/api/ads/${encodeURIComponent(ad.adId)}/generation-inspector?brandId=${encodeURIComponent(brandId)}`
      );
      const gen = resp.inspector?.image?.imageGeneration || null;
      const text = gen?.prompt || '';
      if (!text) {
        toast({
          title: 'No prompt recorded for this ad',
          description: 'This render has no captured image-model prompt, so there is nothing to load. You can still paste a prompt of your own.',
          status: 'warning',
          duration: 6000
        });
        return;
      }
      setImgRaw(text);
      setImgPromptMeta({ model: gen?.model || 'unknown', size: gen?.size || null });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'unknown error';
      toast({ title: 'Could not load the prompt', description: msg, status: 'error', duration: 6000 });
    } finally {
      setImgPromptLoading(false);
    }
  };

  const submitRegen = async () => {
    // Defense in depth: the panel is not rendered for scene reels.
    if (sceneReelAd) return;
    if (!canSubmitRegen) {
      // Emptiness is no longer a reason to land here, so the old
      // "Add a refinement or a raw prompt first" no longer describes anything
      // the code checks. Name the actual blocker instead of a generic refusal.
      toast({
        title: ad.masterAdId
          ? 'This crop is derived from its master'
          : isExported
            ? 'Exported ads are read-only'
            : 'A regeneration is already in progress',
        description: ad.masterAdId
          ? 'Regenerate the master instead and this surface will re-derive from it.'
          : undefined,
        status: 'warning',
        duration: 4000
      });
      return;
    }
    setRegenSubmitting(true);
    try {
      // Raw only rides on a full video re-roll. Refinement is optional
      // when raw is present (API allows empty prompt + videoPromptRaw).
      // When both are present the backend prefers raw (wizard parity).
      const body: Record<string, unknown> = {};
      if (regenPromptTrimmed) body.prompt = regenPromptTrimmed;
      if (ad.kind === 'video') {
        // Explicit and honest: a video regenerate always re-rolls the master.
        // The server ignores this field (regen.resolveEffectiveRegenMode) and
        // reports the mode it will actually bill in its 202.
        body.mode = 'full';
        if (videoFullReroll && regenModel) body.videoModel = regenModel;
        if (videoFullReroll && regenRawTrimmed) body.videoPromptRaw = regenRawTrimmed;
      } else if (imgRawTrimmed) {
        // Full replacement of the image prompt. Backend rejects this together
        // with promptOverride, and ignores the refinement note when it is set.
        body.imagePromptRaw = imgRawTrimmed;
      }
      // TODO(reroll-master cascade, deferred — needs backend/adgen unlock):
      // Today regenerating a video master does NOT refresh its derivatives (Meta
      // crops, funnel retitles, PMax square) — adgen/src/services/
      // adRegenerateService.js explicitly REFUSES regenerate on a derive-only ad
      // (see resolveDeriveFromMaster gate, adRegenerateService.js:478-486/705-713).
      // There is no existing endpoint that can re-derive a stale crop from an
      // updated master. A real fix needs either:
      //   (a) a new adgen-side "re-derive" mode distinct from today's blanket
      //       refusal, re-running the inherit-from-sibling-master flow
      //       (renderer.js's derive path) against an already-'draft' row, or
      //   (b) a lighter "mark stale" flag on derivative Ad rows (no re-render,
      //       just an honest status) surfaced in the UI for an operator to act on
      //       manually — this is the recommended first cut: it's free, reversible,
      //       and doesn't hide a real cost multiplier behind one click the way an
      //       automatic cascade would.
      // Either option needs a new backend/adgen field or endpoint — flagged for
      // explicit owner unlock, not implemented in this PR.
      await apiJson<{ regenerating: boolean; regenerationStage: string; mode: string }>(
        `/api/ads/${encodeURIComponent(ad.adId)}/regenerate?brandId=${encodeURIComponent(brandId)}`,
        {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(body)
        }
      );
      onMutated({
        ...ad,
        regenerating:      true,
        regenerationStage: 'pending'
      });
      setRegenOpen(false);
      setRegen720Open(false);
      setRegenPrompt('');
      setRegenRaw('');
      setScaffoldMeta(null);
      setImgRaw('');
      setImgPromptMeta(null);
      toast({ title: 'Regenerating', description: 'You can keep this open to watch progress.', status: 'info', duration: 3000 });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: 'Regenerate failed to start', description: msg, status: 'error', duration: 6000 });
    } finally {
      setRegenSubmitting(false);
    }
  };

  // Bare video re-render at 720p — no refinement, no model pick. Body is
  // only { videoResolutionOverride: '720p' }; the existing route already
  // treats an empty-prompt regenerate as a first-class plain re-render.
  // If the backend has not landed the field yet, the 400 surfaces here
  // the same way any other regenerate failure does.
  const submitRegen720p = async () => {
    if (ad.kind !== 'video' || sceneReelAd) return;
    if (isExported || isRegenerating || regen720Submitting) return;
    setRegen720Submitting(true);
    try {
      await apiJson<{ regenerating: boolean; regenerationStage: string; mode: string }>(
        `/api/ads/${encodeURIComponent(ad.adId)}/regenerate?brandId=${encodeURIComponent(brandId)}`,
        {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ videoResolutionOverride: '720p' })
        }
      );
      onMutated({
        ...ad,
        regenerating:      true,
        regenerationStage: 'pending'
      });
      setRegen720Open(false);
      setRegenOpen(false);
      toast({ title: 'Regenerating at 720p', description: 'You can keep this open to watch progress.', status: 'info', duration: 3000 });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: 'Regenerate at 720p failed to start', description: msg, status: 'error', duration: 6000 });
    } finally {
      setRegen720Submitting(false);
    }
  };

  const saveCopy = async () => {
    if (!copyDirty || copySaving) return;
    const startedAdId = ad.adId;
    const headline = editHeadline.trim().slice(0, 300);
    const cta_text = editCta.trim().slice(0, 300);
    const copy: { headline?: string | null; cta_text?: string | null } = {};
    if (headline !== (ad.headline || '').trim()) copy.headline = headline || null;
    if (cta_text !== (ad.ctaText || '').trim()) copy.cta_text = cta_text || null;
    if (Object.keys(copy).length === 0) return;

    setCopySaving(true);
    try {
      await apiJson(
        `/api/ads/${encodeURIComponent(startedAdId)}?brandId=${encodeURIComponent(brandId)}`,
        {
          method:  'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ copy })
        }
      );
      // Stale-response guard: operator switched ads mid-flight.
      if (openAdIdRef.current !== startedAdId) return;
      onMutated({
        ...ad,
        headline: copy.headline !== undefined ? (copy.headline ?? null) : ad.headline,
        ctaText:  copy.cta_text !== undefined ? (copy.cta_text ?? null) : ad.ctaText
      });
      toast({ title: 'Copy saved', status: 'success', duration: 2500 });
    } catch (e) {
      if (openAdIdRef.current !== startedAdId) return;
      toast({
        title:       'Copy save failed',
        description: e instanceof Error ? e.message : String(e),
        status:      'error',
        duration:    5000
      });
    } finally {
      setCopySaving(false);
    }
  };

  const rerenderTitle = async () => {
    if (titleRerendering || ad.kind !== 'video') return;
    const startedAdId = ad.adId;
    setTitleRerendering(true);
    try {
      // Live retitle is async (adgen job). POST 202 {jobId}; poll GET until
      // status is done/failed. Ineligible ads still 202 — errors land on the job.
      const kick = await apiJson<{
        jobId?: string;
        status?: string;
        count?: number;
        error?: string;
        errors?: Array<{ id: string; error: string }>;
      }>(`/api/brand/${encodeURIComponent(brandId)}/retitle-videos`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ adIds: [startedAdId] })
      });
      if (openAdIdRef.current !== startedAdId) return;
      const jobId = kick.jobId;
      if (!jobId) {
        const syncErr = kick.error
          || kick.errors?.find(e => e.id === startedAdId)?.error
          || kick.errors?.[0]?.error
          || 'Title re-render did not start';
        throw new Error(syncErr);
      }

      type RetitlePoll = {
        status: 'pending' | 'running' | 'done' | 'failed' | string;
        count?: number;
        progress?: { done: number; total: number };
        results?: Array<{ id: string; ok: boolean; renderUrl?: string | null; error?: string }>;
        errors?: Array<{ id: string; error: string }>;
        error?: string;
        elapsedMs?: number;
      };
      const INTERVAL_MS = 2500;
      const TIMEOUT_MS  = 5 * 60 * 1000;
      const startedAt = Date.now();
      const job = await new Promise<RetitlePoll | null>((resolve, reject) => {
        const tick = async () => {
          if (openAdIdRef.current !== startedAdId) {
            resolve(null);
            return;
          }
          try {
            const s = await apiJson<RetitlePoll>(
              `/api/brand/${encodeURIComponent(brandId)}/retitle-videos/${encodeURIComponent(jobId)}`
            );
            if (s.status === 'done' || s.status === 'failed') {
              resolve(s);
              return;
            }
          } catch (e) {
            // 404 = expired/unknown job (in-memory store); retrying never recovers.
            const status = (e as { status?: number } | undefined)?.status;
            if (status === 404) {
              reject(e instanceof Error ? e : new Error('Retitle job not found or expired'));
              return;
            }
            // Transient failures retry on the next tick (same as pollPreview).
          }
          if (Date.now() - startedAt > TIMEOUT_MS) {
            reject(new Error('Title re-render took longer than 5 minutes — aborted. Try again.'));
            return;
          }
          setTimeout(tick, INTERVAL_MS);
        };
        setTimeout(tick, INTERVAL_MS);
      });
      // Stale-response guard: operator switched ads mid-flight.
      if (!job || openAdIdRef.current !== startedAdId) return;

      if (job.status === 'failed') {
        throw new Error(job.error || 'Title re-render failed');
      }
      const mine = (job.results || []).find(r => r.id === startedAdId);
      if (mine && !mine.ok) {
        throw new Error(mine.error || 'Title re-render failed');
      }
      if (!mine) {
        const seed = (job.errors || []).find(e => e.id === startedAdId);
        throw new Error(seed?.error || job.error || 'This ad was not eligible to re-title');
      }

      const nextUrl = mine.renderUrl ?? null;
      if (nextUrl) {
        onMutated({ ...ad, renderUrl: nextUrl });
        setRenderBust(Date.now());
      } else {
        // Backend may rewrite in place under the same URL — still bust cache.
        setRenderBust(Date.now());
      }
      toast({
        title:       'Title re-rendered',
        description: 'Preview updated with the new title copy.',
        status:      'success',
        duration:    3500
      });
    } catch (e) {
      if (openAdIdRef.current !== startedAdId) return;
      toast({
        title:       'Title re-render failed',
        description: e instanceof Error ? e.message : String(e),
        status:      'error',
        duration:    6000
      });
    } finally {
      setTitleRerendering(false);
    }
  };

  const toggleApprove = async () => {
    setApproving(true);
    try {
      const res = await apiJson<{ ad: { id: string; approved: boolean; approvedAt: string | null } }>(
        `/api/ads/${encodeURIComponent(ad.adId)}/approve?brandId=${encodeURIComponent(brandId)}`,
        {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ approved: !isApproved })
        }
      );
      onMutated({
        ...ad,
        approved:   res.ad.approved,
        approvedAt: res.ad.approvedAt
      });
      toast({
        title:    res.ad.approved ? 'Ad approved' : 'Ad unapproved',
        status:   res.ad.approved ? 'success' : 'info',
        duration: 2500
      });
    } catch (e) {
      toast({
        title:       'Approval failed',
        description: e instanceof Error ? e.message : String(e),
        status:      'error',
        duration:    5000
      });
    } finally {
      setApproving(false);
    }
  };

  const loadAdsets = async () => {
    if (adsets) return;
    setAdsetsLoading(true);
    try {
      const res = await apiJson<{ adsets: MetaAdset[] }>(
        `/api/ads/meta-adsets?brandId=${encodeURIComponent(brandId)}`
      );
      setAdsets(res.adsets || []);
    } catch (e) {
      toast({
        title:       'Could not load Meta AdSets',
        description: e instanceof Error ? e.message : String(e),
        status:      'error',
        duration:    5000
      });
    } finally {
      setAdsetsLoading(false);
    }
  };

  const doExport = async () => {
    if (!chosenAdsetId) {
      toast({ title: 'Pick an AdSet first', status: 'warning', duration: 2500 });
      return;
    }
    const startedAdId = ad.adId;
    const adsetId = chosenAdsetId;
    setPushing(true);
    setPushNotice(null);
    try {
      const result = await apiJson<MetaPushResponse>('/api/ads/push-to-meta', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ brandId, adsetId, adIds: [startedAdId] })
      });
      // Stale-response guard: operator switched ads mid-flight.
      if (openAdIdRef.current !== startedAdId) return;
      const interpreted = interpretMetaPush(result, startedAdId);
      if (interpreted.showAsPushed) {
        // A fresh push belongs to the AdSet just chosen. A refusal that
        // means "already on Meta" keeps the adset the row (or the ad)
        // already names, and only falls back to the picker.
        const metaAdsetId = interpreted.ok
          ? (interpreted.metaAdsetId || adsetId)
          : (interpreted.metaAdsetId || ad.metaAdsetId || adsetId);
        onMutated({
          ...ad,
          metaSyncStatus: 'synced',
          metaAdId:       interpreted.metaAdId || ad.metaAdId,
          metaAdsetId
        });
      }
      setPushNotice({ tone: interpreted.tone, text: interpreted.message });
      toast({
        title: interpreted.ok
          ? 'Exported to Meta'
          : interpreted.showAsPushed
            ? 'Already on Meta'
            : 'Export failed',
        description: interpreted.message,
        status:      interpreted.tone,
        duration:    interpreted.ok ? 3000 : 6000
      });
    } catch (e) {
      if (openAdIdRef.current !== startedAdId) return;
      const description = e instanceof Error ? e.message : String(e);
      setPushNotice({ tone: 'error', text: description });
      toast({
        title:       'Export failed',
        description,
        status:      'error',
        duration:    6000
      });
    } finally {
      setPushing(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="3xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <VStack align="stretch" spacing={1}>
            <HStack spacing={2}>
              <Text fontSize="md" fontWeight="800" color="brand.ink">{ad.headline || ad.template}</Text>
              <Badge colorScheme={workflowBadge.colorScheme} variant="subtle" title={workflowBadge.title}>
                {workflowBadge.text}
              </Badge>
              {sceneReelAd && <SceneReelBadge summary={ad.sceneReel} />}
            </HStack>
            <HStack spacing={2} fontSize="11px" color="brand.muted">
              <Text fontFamily="mono">{ad.template}</Text>
              <Text>·</Text>
              <Text>{ad.aspectRatio}</Text>
              {ad.platformFormat && (<><Text>·</Text><Text>{ad.platformFormat.replace(/_/g, ' ')}</Text></>)}
              <Text>·</Text>
              <Text>{mediaTypeLabel(ad.kind)}</Text>
              {/* Intent profile — most ads carry none, so this conditional
                  item rarely adds width; when absent it's skipped entirely
                  (no orphan "·"), never shown as a raw/undefined token. */}
              {funnelStageLabel(ad.funnelStage) && (
                <>
                  <Text>·</Text>
                  <Text>{funnelStageLabel(ad.funnelStage)}</Text>
                </>
              )}
              <Text>·</Text>
              <Text fontFamily="mono">{ad.adId}</Text>
              <Button
                size="xs" variant="ghost" minW="auto" h="auto" py={0} px={1}
                onClick={() => void navigator.clipboard.writeText(ad.adId).then(
                  () => toast({ title: 'Ad ID copied', status: 'success', duration: 2000 }),
                  () => toast({ title: 'Copy failed', description: ad.adId, status: 'error', duration: 4000 })
                )}
              >
                Copy
              </Button>
            </HStack>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Inert until backend ships masterAdId — renders nothing today.
              A scene reel's 1:1 gets its own link to the 9:16 inside
              SceneStrip instead. */}
          {!sceneReelAd && <MasterLinkCard ad={ad} onOpen={onOpenMaster} />}

          {/* Large preview — the ad inside the placement chrome it will
              actually run in, at handset measure. */}
          {url ? (
            <VStack align="stretch" spacing={2} mb={3}>
              <AdChromeFrame
                chrome={previewChrome}
                ratio={chromeRatio}
                identity={chromeIdentity}
                density="full"
              >
                {ad.kind === 'video' ? (
                  <video
                    key={renderBust || ad.renderUrl || 'video'}
                    src={videoSrc}
                    poster={ad.posterUrl || undefined}
                    controls
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#000' }}
                  />
                ) : (
                  <Image src={url} alt={ad.headline || ad.template} w="100%" h="100%" objectFit="contain" />
                )}
              </AdChromeFrame>
              <HStack spacing={2} justify="center">
                <Text fontSize="2xs" color="brand.muted" flexShrink={0}>Preview as</Text>
                <ChromeSelect value={previewChrome} options={chromeOptions} onChange={setPreviewChrome} />
              </HStack>
            </VStack>
          ) : (
            <Box bg="gray.900" borderRadius="md" overflow="hidden" mb={3}>
              <Box w="100%" h="320px" display="flex" alignItems="center" justifyContent="center" color="gray.400" fontSize="sm">
                No render available
              </Box>
            </Box>
          )}

          {/* Scene reel: per-scene frames, clips, QC, takes and the only
              regenerate a scene reel has (one scene at a time). */}
          {sceneReelAd && (
            <SceneStrip
              ad={ad}
              brandId={brandId}
              retitleFollowStopped={retitleFollowStopped}
              onMutated={onMutated}
              onOpenMaster={onOpenMaster}
            />
          )}

          {/* Editable title copy — PATCH /api/ads/:id {copy}; video ads can
              re-render titles standalone (e.g. after a brand placement-mode
              change) without requiring a prior copy edit. */}
          <VStack align="stretch" spacing={2} mb={3}>
            <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
              Title copy
            </Text>
            <HStack spacing={2} align="center">
              <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" w="80px" flexShrink={0}>Headline</Text>
              <Input
                size="sm"
                value={editHeadline}
                maxLength={300}
                onChange={e => setEditHeadline(e.target.value)}
                placeholder="Headline on the title plate"
                isDisabled={copySaving || titleRerendering || isExported || isRegenerating}
                title={isExported ? 'Exported ads are read-only' : undefined}
              />
            </HStack>
            <HStack spacing={2} align="center">
              <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" w="80px" flexShrink={0}>CTA</Text>
              <Input
                size="sm"
                value={editCta}
                maxLength={300}
                onChange={e => setEditCta(e.target.value)}
                placeholder="CTA text (e.g. SHOP NOW)"
                isDisabled={copySaving || titleRerendering || isExported || isRegenerating}
                title={isExported ? 'Exported ads are read-only' : undefined}
              />
            </HStack>
            {ad.kind !== 'video' && (
              <Text fontSize="11px" color="brand.muted">
                Saved copy applies to future renders — image creatives are not re-rendered here.
              </Text>
            )}
            <HStack spacing={2} justify="flex-end" flexWrap="wrap">
              {titleRerendering && (
                <HStack spacing={1} mr="auto">
                  <Spinner size="xs" color="rsViolet.500" />
                  <Text fontSize="xs" color="brand.muted">Re-rendering title… this can take tens of seconds.</Text>
                </HStack>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => void saveCopy()}
                isLoading={copySaving}
                isDisabled={!copyDirty || titleRerendering || isExported || isRegenerating}
                title={isExported ? 'Exported ads are read-only' : undefined}
              >
                Save copy
              </Button>
              {ad.kind === 'video' && (
                <Button
                  size="sm"
                  variant="brand"
                  onClick={() => void rerenderTitle()}
                  isLoading={titleRerendering}
                  loadingText="Re-rendering…"
                  // Re-title must be runnable standalone (e.g. after a brand
                  // placement-mode change) — only gate on unsaved draft, not
                  // on having just saved a copy edit.
                  isDisabled={copyDirty || copySaving || titleRerendering || isExported || isRegenerating}
                  title={
                    isExported
                      ? 'Exported ads are read-only'
                      : copyDirty
                        ? 'Save pending edits before re-rendering'
                        : undefined
                  }
                >
                  Re-render title
                </Button>
              )}
            </HStack>
          </VStack>

          {/* Retailer product-page link — same treatment as the legacy
              /ads gallery detail (pages/Ads/index.tsx). Null-safe: omitted
              entirely (no row, no dead link) whenever the ad has no
              productId, the product was unlinked/soft-deleted, or the
              product has no productUrl on file. Server-side join is
              brand-scoped (loadProductUrlMap) so this can never surface
              another brand's product URL — see PR #245 / #263. href is
              scheme-gated (http/https only): catalog productUrl is
              tenant-writable and React will not strip javascript:.
              isExternal sets target="_blank" rel="noopener noreferrer". */}
          {ad.productUrl && (
            <Box mb={3}>
              <Text fontSize="9px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
                Retailer product page
              </Text>
              {productHref ? (
                <Link
                  href={productHref}
                  isExternal
                  fontSize="sm"
                  fontWeight="600"
                  color="rsViolet.600"
                  _hover={{ textDecoration: 'underline' }}
                >
                  View on retailer's site ↗
                </Link>
              ) : (
                <Text fontSize="sm" fontWeight="600" color="brand.muted">
                  View on retailer's site ↗
                </Text>
              )}
            </Box>
          )}

          {/* QC-advisory findings. After QC-1 an inspected fail is a
              delivered draft, so this must not key on status==='failed'.
              Paint from the outline flag / isQc verdict. Genuine non-QC
              crashes (titling, render) still use the orange box in
              QcFailureReview. */}
          {showQcFindings && !isRegenerating && (
            <QcFindingsPanel visionQc={ad.visionQc} failure={ad.failure} />
          )}

          {/* Rejected-candidate preview + vouch (override-qc) + audit.
              Shared control — also mounted from Campaigns / CampaignDetail
              / UgcAds / agent AdInspectCard via this same modal. Failure
              text is hidden here when QcFindingsPanel already painted it. */}
          <QcFailureReview
            ad={ad}
            brandId={brandId}
            isRegenerating={isRegenerating}
            showFailureText={!showQcFindings}
            onMutated={(patch) => onMutated({ ...ad, ...patch })}
          />

          {/* Regenerate status banner — fires while a regen worker is
              running. Shows the stage label so the operator knows
              what's happening during long video regens (~5 min). */}
          {isRegenerating && (
            <Box mt={2} mb={3} p={3} borderWidth="1px" borderColor="rsViolet.300" bg="rsViolet.50" borderRadius="md">
              <HStack spacing={2}>
                <Spinner size="sm" color="rsViolet.500" />
                <Text fontSize="sm" fontWeight="700" color="brand.ink">{stageLabel(ad.regenerationStage)}</Text>
              </HStack>
              <Text fontSize="11px" color="brand.muted" mt={1}>
                Keep this open to watch progress, or close — we'll keep regenerating in the background.
              </Text>
            </Box>
          )}
          {lastError && !isRegenerating && (
            <Box mt={2} mb={3} p={3} borderWidth="1px" borderColor="red.300" bg="red.50" borderRadius="md">
              <Text fontSize="sm" fontWeight="700" color="red.700">Last regenerate failed</Text>
              <Text fontSize="11px" color="brand.muted" mt={1}>{lastError}</Text>
            </Box>
          )}

          {typeof ad.derivativeCount === 'number' && ad.derivativeCount > 0 && (
            <Text fontSize="11px" color="brand.muted" mt={2} mb={3}>
              {ad.derivativeCount} derivative {ad.derivativeCount === 1 ? 'ad references' : 'ads reference'} this master.
            </Text>
          )}

          {/* Regenerate input panel — expands when operator clicks
              Regenerate in the footer. Inline so the preview stays
              visible alongside the refinement they're writing. */}
          {regenOpen && !sceneReelAd && (
            <Box mt={2} mb={3} p={3} borderWidth="1px" borderColor="brand.border" borderRadius="md" bg="brand.surface">
              <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
                Regenerate with a refinement prompt
              </Text>
              <Text fontSize="xs" color="brand.muted" mb={2}>
                {ad.kind === 'video'
                  ? 'A refinement is PREPENDED as highest-priority operator direction on top of the canonical camera prompt (product-fidelity and no-text directives stay). Use Advanced below to REPLACE the canonical prompt instead.'
                  : 'A refinement note is applied on top of the auto-composed image prompt. Use Advanced below to REPLACE the whole prompt instead.'}
              </Text>
              {/* When a raw camera prompt is set, refinement is ignored
                  server-side (raw wins). Dim it so the operator sees the
                  same rule the wizard uses: guidance disabled while raw. */}
              <Box
                opacity={rawSuppressesRefinement ? 0.45 : 1}
                pointerEvents={rawSuppressesRefinement ? 'none' : 'auto'}
              >
                <Textarea
                  value={regenPrompt}
                  onChange={(e) => setRegenPrompt(e.target.value)}
                  placeholder={ad.kind === 'video'
                    ? 'e.g. "slow push-in, soft morning light, hold on the bottle label"'
                    : 'e.g. "Swap the headline color to brand purple and tighten the bottom padding."'}
                  maxLength={1000}
                  rows={3}
                  size="sm"
                  bg="white"
                  isDisabled={rawSuppressesRefinement}
                />
                <HStack justify="space-between" mt={1}>
                  <Text fontSize="xs" color="brand.muted">
                    {rawSuppressesRefinement
                      ? 'Disabled while a raw prompt is set (raw replaces the whole prompt).'
                      : ad.kind === 'video'
                        ? 'Prepended onto the canonical camera prompt — does not drop product-fidelity safeguards.'
                        : 'Applied as a refinement note on the image prompt.'}
                  </Text>
                  <Text fontSize="xs" color="brand.muted">{regenPrompt.length}/1000</Text>
                </HStack>
              </Box>
              <HStack mt={2} justify="space-between" align="center">
                {/* NOT a control. This used to be a checkbox, default
                    UNCHECKED, reading "Also re-roll the video (…) — otherwise
                    only the chrome regenerates". No chrome-only path exists:
                    the backend deleted video LIGHT mode in a23801e7 along with
                    the HTML/Puppeteer chrome pipeline it needed, and every
                    video regenerate submits one billable Omni master. So the
                    box offered a cheaper option that was never available and
                    billed the operator anyway. State the real cost instead. */}
                {ad.kind === 'video' ? (
                  <Text fontSize="11px" color="brand.muted" maxW="520px">
                    {ad.masterAdId
                      // This ad IS a derivative (Meta crop / funnel retitle /
                      // PMax square) — adgen's regenerateConsumer refuses a
                      // re-roll on it outright (it has no generation of its
                      // own). That refusal is real, so keep saying so.
                      ? 'This crop is derived from its master and cannot be re-rolled here — regenerate the master instead.'
                      : (() => {
                          const m = regenModel ? videoModels?.find(v => v.slug === regenModel) : null;
                          const cost = m?.estCostPer8s != null ? `~$${m.estCostPer8s.toFixed(2)}` : '~$1.85';
                          return `Regenerating re-rolls the whole video — one new video generation (${cost}, ~5 min). There is no chrome-only option.`;
                        })()}
                  </Text>
                ) : <Box />}
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setRegenOpen(false);
                      setRegenPrompt('');
                      setRegenRaw('');
                      setScaffoldMeta(null);
                      setImgRaw('');
                      setImgPromptMeta(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="brand"
                    onClick={() => void submitRegen()}
                    isLoading={regenSubmitting}
                    isDisabled={!canSubmitRegen}
                    title={
                      ad.masterAdId
                        ? 'This crop is derived from its master and cannot be regenerated here — regenerate the master instead.'
                        : regenHasOperatorText
                          ? 'Re-run with your refinement applied on top of the prompt'
                          : 'Re-run this ad against the exact same prompt — no changes'
                    }
                  >
                    {regenHasOperatorText ? 'Regenerate with refinement' : 'Regenerate'}
                  </Button>
                </HStack>
              </HStack>

              {videoFullReroll && !ad.masterAdId && (
                <Box mt={2} p={3} borderWidth="1px" borderColor="blue.300" bg="blue.50" borderRadius="md">
                  <Text fontSize="sm" fontWeight="700" color="blue.700">Re-roll does not update derivatives</Text>
                  <Text fontSize="11px" color="brand.muted" mt={1}>
                    Re-rolling this video does not update any derivative crops or funnel-stage variants already
                    rendered from it (Meta square/4:5/Reels, PMax square, funnel retitles) — they'll keep showing
                    the previous video until specifically re-rendered. There's no automatic cascade yet.
                  </Text>
                </Box>
              )}

              {/* Model picker — every video regenerate re-rolls, so this is
                  always live for video. '' rides the brand/product default
                  chain server-side. */}
              {ad.kind === 'video' && (
                <HStack mt={2} spacing={2} align="center">
                  <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" flexShrink={0}>
                    Video model
                  </Text>
                  <Select
                    size="sm"
                    maxW="360px"
                    bg="white"
                    value={regenModel}
                    onChange={(e) => setRegenModel(e.target.value)}
                    isDisabled={videoModels == null}
                  >
                    <option value="">Brand default</option>
                    {(videoModels || []).map(m => (
                      <option key={m.slug} value={m.slug}>
                        {m.label}{m.default ? ' (system default)' : ''}
                      </option>
                    ))}
                  </Select>
                </HStack>
              )}

              {/* Advanced — canonical / raw camera prompt. Collapsed by
                  default, but never dimmed: the video IS re-rolled on every
                  regenerate, so the camera prompt always takes effect. It
                  used to be gated (and pointer-events-disabled) behind the
                  re-roll checkbox, which meant an operator who left the box
                  unchecked was billed for a re-roll they could not steer.
                  Copy and interaction match the wizard's Advanced disclosure
                  so the two screens feel the same. */}
              {ad.kind === 'video' && (
                <Box mt={3}>
                  <Accordion allowToggle>
                    <AccordionItem border="none">
                      <AccordionButton
                        px={0}
                        py={1}
                        _hover={{ bg: 'transparent' }}
                        _expanded={{ color: 'rsViolet.700' }}
                      >
                        <Box flex="1" textAlign="left">
                          <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
                            Advanced — canonical prompt
                          </Text>
                        </Box>
                        <AccordionIcon color="brand.muted" />
                      </AccordionButton>
                      <AccordionPanel px={0} pb={1} pt={2}>
                          <>
                            <HStack justify="space-between" mb={2} wrap="wrap" spacing={2}>
                              <Text fontSize="xs" color="orange.600" fontWeight="600" maxW="520px">
                                Raw REPLACES the entire camera prompt — product-fidelity and no-text safeguards are dropped unless you keep them in the text. A refinement (above) only PREPENDS and keeps those safeguards.
                              </Text>
                              <Button
                                size="xs"
                                variant="outline"
                                onClick={() => void loadRegenScaffold()}
                                isLoading={scaffoldLoading}
                                loadingText="Loading…"
                              >
                                Load canonical scaffold
                              </Button>
                            </HStack>
                            <Textarea
                              size="sm"
                              rows={6}
                              maxLength={4000}
                              value={regenRaw}
                              onChange={(e) => setRegenRaw(e.target.value)}
                              placeholder="Paste or edit the full camera prompt. Leave empty to use the canonical prompt (+ refinement above)."
                              fontFamily="mono"
                              fontSize="11px"
                              bg="white"
                            />
                            <HStack justify="space-between" mt={1}>
                              <Text fontSize="xs" color="brand.muted">
                                {scaffoldMeta
                                  ? `Model: ${scaffoldMeta.model} · byte cap: ${scaffoldMeta.byteCap}`
                                  : 'Load the scaffold to see model + byte-cap for this ad, then edit rather than write from scratch.'}
                              </Text>
                              <Text fontSize="xs" color="brand.muted">
                                {regenRaw.length}/4000
                              </Text>
                            </HStack>
                          </>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </Box>
              )}

              {/* Advanced — raw IMAGE prompt. Static ads only. Always
                  interactive (no full-re-roll gate on the image path). Loads
                  the verbatim prompt that produced the current render, not a
                  re-derived scaffold.

                  OPEN BY DEFAULT, unlike the video block above. That block is
                  collapsed because it is inert until "Also re-roll the video"
                  is ticked, so showing it expanded would advertise a control
                  that does nothing. This one is live the moment the panel
                  opens — and shipped collapsed it was missed entirely
                  (owner, 2026-08-04: "I don't see the raw prompt option").
                  Mirroring the video block's collapse was copying a
                  constraint that does not apply here. */}
              {ad.kind !== 'video' && (
                <Box mt={3}>
                  <Accordion allowToggle defaultIndex={[0]}>
                    <AccordionItem border="none">
                      <AccordionButton
                        px={0}
                        py={1}
                        _hover={{ bg: 'transparent' }}
                        _expanded={{ color: 'rsViolet.700' }}
                      >
                        <Box flex="1" textAlign="left">
                          <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
                            Advanced — image prompt
                          </Text>
                        </Box>
                        <AccordionIcon color="brand.muted" />
                      </AccordionButton>
                      <AccordionPanel px={0} pb={1} pt={2}>
                        <HStack justify="space-between" mb={2} wrap="wrap" spacing={2}>
                          <Text fontSize="xs" color="orange.600" fontWeight="600" maxW="520px">
                            Raw REPLACES the entire image prompt — product-fidelity, the exact-copy contract, the no-invented-logo rules and the safe-box geometry are dropped unless you keep them in the text. Size, crop, reference images and logo placement are unaffected. A refinement (above) only appends and keeps all of it.
                          </Text>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => void loadCurrentImagePrompt()}
                            isLoading={imgPromptLoading}
                            loadingText="Loading…"
                          >
                            Load the prompt that made this ad
                          </Button>
                        </HStack>
                        <Textarea
                          size="sm"
                          rows={10}
                          maxLength={40000}
                          value={imgRaw}
                          onChange={(e) => setImgRaw(e.target.value)}
                          placeholder="Paste or edit the full image prompt. Leave empty to use the auto-composed prompt (+ refinement above)."
                          fontFamily="mono"
                          fontSize="11px"
                          bg="white"
                        />
                        <HStack justify="space-between" mt={1}>
                          <Text fontSize="xs" color="brand.muted">
                            {imgPromptMeta
                              ? `Model: ${imgPromptMeta.model}${imgPromptMeta.size ? ` · gen ${imgPromptMeta.size}` : ''}`
                              : 'Load the prompt to see the model + generation size for this ad, then edit rather than write from scratch.'}
                          </Text>
                          <Text fontSize="xs" color="brand.muted">
                            {imgRaw.length}/40000
                          </Text>
                        </HStack>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </Box>
              )}
            </Box>
          )}

          {/* Regenerate at 720p — video-only quality-repair action.
              Same POST as the refinement panel, body is only
              { videoResolutionOverride: '720p' }. Confirm step matches
              the existing Regenerate friction (open panel, then submit);
              no reason textarea — this is a paid re-render, not an
              audit-trail override. Not gated on status === 'failed'. */}
          {ad.kind === 'video' && regen720Open && !sceneReelAd && (
            <Box mt={2} mb={3} p={3} borderWidth="1px" borderColor="brand.border" borderRadius="md" bg="brand.surface">
              <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
                Regenerate at 720p
              </Text>
              <Text fontSize="xs" color="brand.muted" mb={2}>
                This re-rolls the video at 720p — one billable video generation, same cost as Regenerate (~5 min). No refinement prompt is sent.
              </Text>
              <HStack justify="flex-end" spacing={2}>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setRegen720Open(false)}
                  isDisabled={regen720Submitting}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="brand"
                  onClick={() => void submitRegen720p()}
                  isLoading={regen720Submitting}
                  isDisabled={isExported || isRegenerating}
                  title={
                    isExported     ? 'Exported ads are read-only'
                  : isRegenerating ? 'Already regenerating'
                  : undefined
                  }
                >
                  Regenerate at 720p
                </Button>
              </HStack>
            </Box>
          )}

          {/* AdSet picker — only renders once the operator clicks Export
              for the first time. Avoids the legacy /ads-page pattern of
              eager-fetching even when they were just browsing. */}
          {adsets !== null && !isExported && (
            <Box pt={2} borderTopWidth="1px" borderColor="brand.border">
              <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={1}>
                Push to AdSet
              </Text>
              {adsets.length === 0 ? (
                <Text fontSize="sm" color="brand.muted">No Meta AdSets found for this brand. Connect Meta or create an AdSet first.</Text>
              ) : (
                <Box as="select"
                     aria-label="Meta AdSet"
                     value={chosenAdsetId}
                     onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setChosenAdsetId(e.target.value)}
                     borderWidth="1px" borderColor="brand.border" borderRadius="md" p={2}
                     fontSize="sm" w="100%" bg="white">
                  <option value="">— pick an AdSet —</option>
                  {adsets.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name}{a.campaignName ? ` (${a.campaignName})` : ''}{a.status ? ` — ${a.status}` : ''}
                    </option>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {pushNotice && (
            <Box
              data-testid="meta-push-notice"
              role="status"
              mt={3}
              p={3}
              borderWidth="1px"
              borderRadius="md"
              borderColor={
                pushNotice.tone === 'success' ? 'green.200'
                : pushNotice.tone === 'warning' ? 'orange.200'
                : 'red.200'
              }
              bg={
                pushNotice.tone === 'success' ? 'green.50'
                : pushNotice.tone === 'warning' ? 'orange.50'
                : 'red.50'
              }
            >
              <Text
                fontSize="sm"
                whiteSpace="pre-wrap"
                color={
                  pushNotice.tone === 'success' ? 'green.800'
                  : pushNotice.tone === 'warning' ? 'orange.800'
                  : 'red.800'
                }
              >
                {pushNotice.text}
              </Text>
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2}>
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <Button variant="outline" onClick={() => setShowInspector(true)}>
              Generation details
            </Button>
            {!sceneReelAd && (
            <Button
              variant="outline"
              onClick={() => {
                setRegenOpen(v => !v);
                setRegen720Open(false);
              }}
              isDisabled={isExported || isRegenerating || !!ad.masterAdId}
              title={
                isExported     ? 'Exported ads are read-only'
              : isRegenerating ? 'Already regenerating'
              : ad.masterAdId  ? 'This crop is derived from its master and cannot be regenerated here — regenerate the master instead.'
              : undefined
              }
            >
              Regenerate
            </Button>
            )}
            {ad.kind === 'video' && !sceneReelAd && (
              <Button
                variant="outline"
                onClick={() => {
                  setRegen720Open(v => !v);
                  setRegenOpen(false);
                }}
                isDisabled={isExported || isRegenerating || !!ad.masterAdId}
                title={
                  isExported     ? 'Exported ads are read-only'
                : isRegenerating ? 'Already regenerating'
                : ad.masterAdId  ? 'This crop is derived from its master and cannot be regenerated here — regenerate the master instead.'
                : 'Re-roll this video at 720p — same cost as Regenerate'
                }
              >
                Regenerate at 720p
              </Button>
            )}
            <Button
              variant={isApproved ? 'outline' : 'brand'}
              onClick={toggleApprove}
              isLoading={approving}
              isDisabled={isExported || isRegenerating}
            >
              {isApproved ? 'Unapprove' : 'Approve'}
            </Button>
            {isExported ? (
              <Badge colorScheme="blue" variant="subtle" px={3} py={1} fontSize="11px">
                Synced to Meta
              </Badge>
            ) : adsets === null ? (
              <Button
                variant="brand"
                onClick={loadAdsets}
                isLoading={adsetsLoading}
                isDisabled={!isApproved}
                title={!isApproved ? 'Approve before exporting' : undefined}
              >
                Export
              </Button>
            ) : (
              <Button
                variant="brand"
                onClick={doExport}
                isLoading={pushing}
                isDisabled={!chosenAdsetId || !isApproved}
              >
                Push to Meta
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
      {showInspector && (
        <ModalErrorBoundary what="generation details" onDismiss={() => setShowInspector(false)}>
          <GenerationInspectorModal adId={ad.adId} brandId={brandId} onClose={() => setShowInspector(false)} />
        </ModalErrorBoundary>
      )}
    </Modal>
  );
}

function KpiTile({
  label, value, sub, progressPct, tooltip
}: {
  label:       string;
  value:       number | string;
  sub?:        string;
  progressPct?: number | null;
  tooltip?:    string;
}) {
  return (
    <Card variant="outline" title={tooltip || undefined}>
      <CardBody py={3} px={4}>
        <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.08em" color="brand.muted" mb={1}>
          {label}
        </Text>
        <Text fontSize="2xl" fontWeight="800" color="brand.ink" lineHeight="1.1">
          {value}
        </Text>
        {sub && <Text fontSize="11px" color="brand.muted" mt={0.5} noOfLines={1}>{sub}</Text>}
        {typeof progressPct === 'number' && (
          <Progress value={progressPct} size="xs" colorScheme="purple" mt={2} borderRadius="md" />
        )}
      </CardBody>
    </Card>
  );
}

function ProductRowView({
  row, expanded, expansion, expansionLoading, selected,
  onToggleSelect, onExpand, onGenerate, onOpenAd
}: {
  row:             ProductRow;
  expanded:        boolean;
  expansion:       ExpansionResponse | null;
  expansionLoading: boolean;
  selected:        boolean;
  onToggleSelect:  () => void;
  onExpand:        () => void;
  onGenerate:      () => void;
  onOpenAd:        (ad: ExpansionAd) => void;
}) {
  const cov = coverageLabel(row.coveragePct, { attempted: row.adCount, inFlight: row.inFlightCount });
  return (
    <Box bg={expanded ? 'rsViolet.50' : undefined}>
      <HStack
        px={4}
        py={3}
        spacing={4}
        align="center"
        cursor="pointer"
        _hover={{ bg: expanded ? undefined : 'gray.50' }}
        onClick={onExpand}
      >
        <Checkbox isChecked={selected} onChange={(e) => { e.stopPropagation(); onToggleSelect(); }} onClick={(e) => e.stopPropagation()} />
        <HStack flex="2 1 0" spacing={3} align="center" minW={0}>
          {row.imageUrl ? (
            <Image
              src={row.imageUrl}
              alt={row.title}
              boxSize="48px"
              objectFit="cover"
              borderRadius="md"
              borderWidth="1px"
              borderColor="brand.border"
              flexShrink={0}
            />
          ) : (
            <Box boxSize="48px" bg="gray.100" borderRadius="md" flexShrink={0} />
          )}
          <VStack align="stretch" spacing={1} minW={0}>
            <Text fontSize="sm" fontWeight="700" color="brand.ink" noOfLines={1}>{row.title}</Text>
            <HStack spacing={2} fontSize="11px" color="brand.muted">
              {row.price != null && <Text>{priceLabel(row.price, row.currency)}</Text>}
              {row.size && <><Text>·</Text><Text>{row.size}</Text></>}
              {row.category && <><Text>·</Text><Text noOfLines={1}>{row.category}</Text></>}
            </HStack>
            {Array.isArray(row.campaignChips) && row.campaignChips.length > 0 && (
              <Wrap spacing={1.5} pt={1} onClick={(e) => e.stopPropagation()}>
                {row.campaignChips.slice(0, 4).map(c => (
                  <WrapItem key={c.id}>
                    <Badge
                      as={RouterLink}
                      to={`/campaigns/${c.id}`}
                      variant="subtle"
                      colorScheme="purple"
                      fontSize="10px"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      textTransform="none"
                      fontWeight="600"
                      _hover={{ bg: 'rsViolet.100', textDecoration: 'none' }}
                      title={c.kind ? `${c.name} · ${c.kind}` : c.name}
                    >
                      {c.name}
                    </Badge>
                  </WrapItem>
                ))}
                {row.campaignChips.length > 4 && (
                  <WrapItem>
                    <Badge variant="outline" fontSize="10px" px={2} py={0.5} borderRadius="md" fontWeight="500">
                      +{row.campaignChips.length - 4}
                    </Badge>
                  </WrapItem>
                )}
              </Wrap>
            )}
          </VStack>
        </HStack>
        <Box flex="1 1 0">
          <Text fontSize="sm" fontWeight="700" color={cov.color === 'gray' ? 'brand.muted' : 'brand.ink'}>
            {row.coveragePct}%
          </Text>
          <Progress value={row.coveragePct} size="xs" colorScheme={cov.color === 'gray' ? 'gray' : cov.color === 'orange' ? 'orange' : 'green'} mt={1} borderRadius="md" />
          <Text fontSize="10px" color="brand.muted" mt={0.5}>{cov.label}</Text>
        </Box>
        <Box flex="1 1 0" textAlign="center">
          <Text fontSize="sm" fontWeight="700" color="brand.ink">{row.campaignCount}</Text>
          {row.campaignCount > 0 && <Text fontSize="10px" color="brand.muted">Active</Text>}
        </Box>
        <Box flex="1 1 0" textAlign="center">
          <Text fontSize="sm" fontWeight="700" color="brand.ink">{row.adCount}</Text>
          {row.readyToExport > 0 && <Text fontSize="10px" color="brand.muted">{row.readyToExport} ready</Text>}
        </Box>
        <Box flex="1 1 0">
          <Text fontSize="11px" color="brand.muted">{relativeTime(row.lastActivityAt)}</Text>
        </Box>
        <HStack flex="1.4 1 0" justify="flex-end" onClick={(e) => e.stopPropagation()}>
          <Button size="xs" variant="outline" onClick={onGenerate}>
            Generate Ads
          </Button>
        </HStack>
      </HStack>

      {/* Inline expansion */}
      {expanded && (
        <Box px={4} pb={4} borderTopWidth="1px" borderColor="brand.border" bg="white">
          {expansionLoading && (
            <HStack py={6} justify="center">
              <Spinner size="sm" />
              <Text fontSize="sm" color="brand.muted">Loading campaigns + ads…</Text>
            </HStack>
          )}
          {!expansionLoading && expansion && (
            <ExpansionPanel expansion={expansion} onOpenAd={onOpenAd} />
          )}
        </Box>
      )}
    </Box>
  );
}

// UGC-ads Phase 4 filter — splits the expansion into all / product shots /
// UGC ads. Product shots = variantKind ≠ 'ugc' (covers the whole catalog
// pipeline). UGC = variantKind === 'ugc'. Kept scoped to this component
// so a stale campaign selection can't drift into the URL.
type VariantFilter = 'all' | 'product' | 'ugc';

// GET /api/catalog/:id/ads-detail caps its aggregation at $limit: 60
// across every campaign and status for the product, and both
// `expansion.ads` and the per-campaign `adCount` on ExpansionCampaign
// are derived from that already-capped array. A product with more than
// 60 non-archived ads (easily reached — one generation run alone can
// produce dozens) gets silently undercounted campaign badges with no
// signal that more ads exist. Can't fix the backend ceiling from the
// frontend, but the counts below must stop implying they're exhaustive
// once the fetch itself hit the cap.
const PRODUCT_ADS_FETCH_LIMIT = 60;

function ExpansionPanel({ expansion, onOpenAd }: { expansion: ExpansionResponse; onOpenAd: (ad: ExpansionAd) => void }) {
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
    expansion.campaigns[0]?.campaignId ?? null
  );
  const [variantFilter, setVariantFilter] = useState<VariantFilter>('all');
  // Defaults to the existing grouped behavior — this only adds an option,
  // it never changes what an operator sees without choosing to.
  const [groupMode, setGroupMode] = useState<AdGroupMode>(DEFAULT_AD_GROUP_MODE);

  const filteredAds = useMemo(() => {
    let list = expansion.ads;
    if (selectedCampaignId) list = list.filter(a => a.campaignId === selectedCampaignId);
    if (variantFilter === 'ugc')     list = list.filter(a => a.variantKind === 'ugc');
    if (variantFilter === 'product') list = list.filter(a => a.variantKind !== 'ugc');
    return list;
  }, [expansion.ads, selectedCampaignId, variantFilter]);

  const variantCounts = useMemo(() => {
    // Compute against the campaign-filtered set so the chip counts
    // reflect what would appear if the operator flipped only the
    // variant chip. Matches the UGC Ads page chip semantics.
    const scoped = selectedCampaignId
      ? expansion.ads.filter(a => a.campaignId === selectedCampaignId)
      : expansion.ads;
    let ugc = 0, product = 0;
    for (const a of scoped) (a.variantKind === 'ugc' ? ugc++ : product++);
    return { all: scoped.length, ugc, product };
  }, [expansion.ads, selectedCampaignId]);

  // Group filtered ads by section. Order matters in the UI:
  // Draft first (operator's work queue), then Approved (export-ready),
  // then Exported (audit/reference).
  const grouped = useMemo(() => {
    const draft:    ExpansionAd[] = [];
    const approved: ExpansionAd[] = [];
    const exported: ExpansionAd[] = [];
    for (const ad of filteredAds) {
      const s = sectionFor(ad);
      if (s === 'draft') draft.push(ad);
      else if (s === 'approved') approved.push(ad);
      else exported.push(ad);
    }
    return { draft, approved, exported };
  }, [filteredAds]);

  if (expansion.campaigns.length === 0 && expansion.ads.length === 0) {
    return (
      <Box py={4}>
        <Text fontSize="sm" color="brand.muted">No ads generated for this product yet.</Text>
      </Box>
    );
  }

  // See PRODUCT_ADS_FETCH_LIMIT above — expansion.ads is the backend's
  // already-capped aggregation, so hitting the cap means this product
  // may have more non-archived ads than the fetch returned.
  const atFetchCeiling = expansion.ads.length >= PRODUCT_ADS_FETCH_LIMIT;

  return (
    <HStack align="flex-start" spacing={4} pt={4}>
      {/* Campaign sidebar */}
      <VStack align="stretch" spacing={1} flex="0 0 220px">
        <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.08em" color="brand.muted" mb={1}>
          Campaigns
        </Text>
        {atFetchCeiling && (
          <Text fontSize="10px" color="brand.muted" mb={1}>
            Showing the {PRODUCT_ADS_FETCH_LIMIT} most recent ads for this product — it may have more, and counts below may undercount campaigns with older ads.
          </Text>
        )}
        <CampaignRow
          label="All Campaigns"
          adCount={expansion.ads.length}
          atLeast={atFetchCeiling}
          selected={selectedCampaignId === null}
          onClick={() => setSelectedCampaignId(null)}
        />
        {expansion.campaigns.map(c => (
          <CampaignRow
            key={c.campaignId}
            label={c.name}
            adCount={c.adCount}
            status={c.status}
            selected={c.campaignId === selectedCampaignId}
            onClick={() => setSelectedCampaignId(c.campaignId)}
          />
        ))}
      </VStack>

      {/* Ads — grouped Draft / Approved / Exported. Empty sections
          hide entirely so the panel stays compact when only one
          category exists. */}
      <VStack align="stretch" flex="1 1 0" spacing={4}>
        <HStack spacing={2} flexWrap="wrap" justify="space-between" align="center">
          <HStack spacing={2} flexWrap="wrap">
            <VariantChip label="All"          n={variantCounts.all}     active={variantFilter === 'all'}     onClick={() => setVariantFilter('all')} />
            <VariantChip label="Product shots" n={variantCounts.product} active={variantFilter === 'product'} onClick={() => setVariantFilter('product')} />
            <VariantChip label="UGC ads"      n={variantCounts.ugc}     active={variantFilter === 'ugc'}     onClick={() => setVariantFilter('ugc')} />
          </HStack>
          <AdGroupModeToggle mode={groupMode} onChange={setGroupMode} />
        </HStack>
        {filteredAds.length === 0 && (
          <Text fontSize="sm" color="brand.muted">No ads in this campaign yet.</Text>
        )}
        {groupMode === 'grouped' ? (
          <>
            <AdSectionGrid
              label="Draft"
              tint="purple"
              ads={grouped.draft}
              onOpenAd={onOpenAd}
            />
            <AdSectionGrid
              label="Approved"
              tint="green"
              ads={grouped.approved}
              onOpenAd={onOpenAd}
            />
            <AdSectionGrid
              label="Exported"
              tint="blue"
              ads={grouped.exported}
              onOpenAd={onOpenAd}
            />
          </>
        ) : (
          // Flat newest-first: filteredAds is already API-ordered by
          // recency (see PRODUCT_ADS_FETCH_LIMIT), so no client re-sort —
          // just render it without the Draft/Approved/Exported split.
          <AdSectionGrid
            label="All"
            tint="gray"
            ads={filteredAds}
            onOpenAd={onOpenAd}
          />
        )}
      </VStack>
    </HStack>
  );
}

// One status section of the expansion. Hides when empty so a product
// with only drafts doesn't render two empty stubs.
//
// Was a hardcoded `ads.slice(0, 12)` with no disclosure of the cut —
// a product with 39 draft ads showed a "DRAFT 39" badge sitting directly
// above a grid of exactly 12 tiles, and the other 27 were unreachable from
// this screen (they were only visible via the legacy /ads gallery). Mirrors
// the UgcAds expansion's ADS_INITIAL / "View all N" pattern: an initial cap
// keeps a product with hundreds of ads cheap to render, and every ad past
// the cap stays reachable via the button, whose count matches the badge
// above it — no more silent gap between the two.
const AD_SECTION_INITIAL = 20;

export function AdSectionGrid({
  label, tint, ads, onOpenAd
}: {
  label:    string;
  // 'gray' is the flat "All" view (AdGroupMode === 'flat') — a neutral tint
  // for a section that isn't one of the Draft/Approved/Exported buckets.
  tint:     'purple' | 'green' | 'blue' | 'gray';
  ads:      ExpansionAd[];
  onOpenAd: (ad: ExpansionAd) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  if (!ads.length) return null;
  const labelColor = tint === 'purple' ? 'rsViolet.600'
                   : tint === 'green'  ? 'green.700'
                   : tint === 'blue'   ? 'blue.700'
                   :                     'gray.700';
  const visible = showAll ? ads : ads.slice(0, AD_SECTION_INITIAL);
  return (
    <Box>
      <HStack spacing={2} mb={2} align="center">
        <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.08em" color={labelColor}>
          {label}
        </Text>
        <Badge fontSize="9px" colorScheme={tint} variant="subtle">{ads.length}</Badge>
      </HStack>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
        {visible.map(ad => (
          <Box key={ad.adId} onClick={() => onOpenAd(ad)} cursor="pointer">
            <AdThumbnail ad={ad} />
          </Box>
        ))}
      </SimpleGrid>
      {!showAll && ads.length > AD_SECTION_INITIAL && (
        <Button
          size="xs"
          variant="link"
          onClick={() => setShowAll(true)}
          mt={2}
        >
          {/* label="All" (the flat newest-first view) has no status word to
              qualify "ads" with — "View all N all ads" reads as a typo. */}
          {label === 'All' ? `View all ${ads.length} ads` : `View all ${ads.length} ${label.toLowerCase()} ads`}
        </Button>
      )}
    </Box>
  );
}

function CampaignRow({
  label, adCount, status, selected, onClick, atLeast
}: {
  label:    string;
  adCount:  number;
  status?:  string | null;
  selected: boolean;
  onClick:  () => void;
  // True when adCount is known to be a floor, not an exact total — the
  // ads-detail fetch hit its own cap, so there may be more (see
  // PRODUCT_ADS_FETCH_LIMIT above). Renders "N+ ads" instead of "N ads".
  atLeast?: boolean;
}) {
  return (
    <HStack
      px={2}
      py={2}
      spacing={2}
      borderRadius="md"
      cursor="pointer"
      bg={selected ? 'rsViolet.100' : 'transparent'}
      _hover={{ bg: selected ? 'rsViolet.100' : 'gray.50' }}
      onClick={onClick}
      justify="space-between"
    >
      <HStack spacing={2} minW={0}>
        <Box w="6px" h="6px" borderRadius="full" bg={selected ? 'rsViolet.500' : 'brand.border'} flexShrink={0} />
        <Text fontSize="13px" fontWeight={selected ? '700' : '500'} color="brand.ink" noOfLines={1}>{label}</Text>
        {status && (
          <Badge fontSize="9px" colorScheme={/active|enabled|running/i.test(status) ? 'green' : 'gray'} variant="subtle">
            {status}
          </Badge>
        )}
      </HStack>
      <Text fontSize="11px" color="brand.muted" flexShrink={0}>{adCount}{atLeast ? '+' : ''} ad{adCount === 1 && !atLeast ? '' : 's'}</Text>
    </HStack>
  );
}

// UGC badge overlaid on an ad thumbnail (Phase 4). Renders as a compact
// pill in the top-left; hover expands a preview of the source Media
// (image or short muted video loop). Clicking deep-links to
// /ugc-ads?mediaId=<id>. stopPropagation prevents the underlying
// thumbnail's onClick (the generation-inspector modal) from also firing.
function UgcBadge({
  mediaId, sourceMedia
}: {
  mediaId:     string;
  sourceMedia: { fileUrl: string | null; fileType: string | null } | null;
}) {
  const [hover, setHover] = useState(false);
  return (
    <Box
      position="absolute"
      top={1.5}
      left={1.5}
      zIndex={2}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <RouterLink
        to={`/ugc-ads?mediaId=${mediaId}`}
        onClick={(e) => e.stopPropagation()}
        style={{ textDecoration: 'none' }}
      >
        <Badge
          colorScheme="purple"
          variant="solid"
          fontSize="9px"
          px={1.5}
          py={0.5}
          borderRadius="full"
          cursor="pointer"
          textTransform="uppercase"
          letterSpacing="0.04em"
        >
          UGC
        </Badge>
      </RouterLink>
      {hover && sourceMedia?.fileUrl && (
        <Box
          position="absolute"
          top="22px"
          left={0}
          w="180px"
          borderWidth="1px"
          borderColor="brand.border"
          borderRadius="md"
          overflow="hidden"
          bg="brand.surface"
          boxShadow="0 8px 24px rgba(0,0,0,0.18)"
          zIndex={3}
          pointerEvents="none"
        >
          {sourceMedia.fileType === 'video' ? (
            <video
              src={sourceMedia.fileUrl}
              muted
              loop
              autoPlay
              playsInline
              style={{ display: 'block', width: '100%', maxHeight: 240, objectFit: 'cover' }}
            />
          ) : (
            <Image
              src={sourceMedia.fileUrl}
              alt="Source UGC"
              w="100%"
              maxH="240px"
              objectFit="cover"
              display="block"
            />
          )}
        </Box>
      )}
    </Box>
  );
}

function VariantChip({ label, n, active, onClick }: { label: string; n: number; active: boolean; onClick: () => void }) {
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      px={3}
      py={1}
      borderWidth="1px"
      borderColor={active ? 'rsViolet.500' : 'brand.border'}
      bg={active ? 'rsViolet.500' : 'white'}
      color={active ? 'white' : 'brand.ink'}
      borderRadius="full"
      fontSize="11px"
      fontWeight="700"
      cursor="pointer"
    >
      {label}
      <Badge ml={2} fontSize="9px" colorScheme={active ? 'whiteAlpha' : 'gray'} variant="subtle">
        {n}
      </Badge>
    </Box>
  );
}

// Grouped-by-status vs. flat-newest-first toggle for the ads expansion.
// Shared by ProductAds and Campaigns so the control looks and behaves
// identically on both pages (same duplication pattern as sectionFor /
// AdSectionGrid above — see the file header comment on why this repo
// hasn't extracted a shared expansion hook yet).
export function AdGroupModeToggle({
  mode, onChange
}: {
  mode:     AdGroupMode;
  onChange: (mode: AdGroupMode) => void;
}) {
  return (
    <ButtonGroup isAttached variant="outline" size="xs">
      <Button
        variant={mode === 'grouped' ? 'softBrand' : 'outline'}
        onClick={() => onChange('grouped')}
      >
        Grouped by status
      </Button>
      <Button
        variant={mode === 'flat' ? 'softBrand' : 'outline'}
        onClick={() => onChange('flat')}
      >
        Newest first
      </Button>
    </ButtonGroup>
  );
}

// Shared onOpenMaster resolver for every AdDetailModal mount site (there are
// five). A derivative and its master always share productId, so a caller
// that already has a full per-product/per-campaign ad list in memory should
// pass it as `siblingAds` — the common case, resolved with no extra
// request. When it's absent, not yet loaded, or the master isn't in it (a
// real possibility: several list endpoints cap at 60-200 rows sorted by
// recency, and a master is often the OLDER sibling — see catalog.js
// ads-detail's $limit:60), fall back to GET /api/ads/:id (the existing
// single-ad detail route, already used elsewhere in this file) rather than
// silently doing nothing. Reports failure via toast instead of navigating
// to a page that was never going to show the result (navigating to the
// CURRENT route is a no-op in React Router — it doesn't remount).
export async function resolveAndOpenMaster({
  masterAdId,
  brandId,
  siblingAds,
  setOpenAd,
  toast,
  isCurrent
}: {
  masterAdId: string;
  brandId: string;
  siblingAds?: ExpansionAd[] | null;
  setOpenAd: (ad: ExpansionAd) => void;
  toast: ReturnType<typeof useToast>;
  // Late-arrival guard for the network fallback below, same idea every
  // onMutated handler in this file already uses — capture the CURRENTLY
  // open ad id at click time (before the `await`) and check it's still
  // current after. Without this, closing the modal or opening a different
  // ad while the fetch is in flight would still land the master into
  // whatever's open by the time it resolves. Optional only because the
  // in-memory `siblingAds` hit path never awaits, so it can't race.
  isCurrent?: () => boolean;
}): Promise<void> {
  const found = siblingAds?.find(a => a.adId === masterAdId);
  if (found) {
    setOpenAd(found);
    return;
  }
  try {
    const res = await apiJson<{ ad: any }>(
      `/api/ads/${encodeURIComponent(masterAdId)}?brandId=${encodeURIComponent(brandId)}`
    );
    if (isCurrent && !isCurrent()) return;
    if (res?.ad) {
      setOpenAd(toExpansionAd(res.ad));
      return;
    }
  } catch {
    // fall through to the toast below
  }
  if (isCurrent && !isCurrent()) return;
  toast({ title: 'Could not open master ad', status: 'error', duration: 4000 });
}

export function MasterLinkCard({
  ad,
  onOpen
}: {
  ad: ExpansionAd;
  onOpen: (adId: string) => void;
}) {
  if (!ad.masterAdId) return null;
  const masterStatusBadge = ad.masterStatus
    ? resolveAdStatusBadge(ad.masterStatus, ad.kind, null)
    : null;
  const formatLabel = ad.masterPlatformFormat
    ? ad.masterPlatformFormat.replace(/_/g, ' ')
    : null;
  return (
    <HStack
      as="button"
      type="button"
      onClick={() => onOpen(ad.masterAdId as string)}
      spacing={3}
      mb={3}
      p={2}
      w="100%"
      textAlign="left"
      borderWidth="1px"
      borderColor="brand.border"
      borderRadius="md"
      bg="gray.50"
      cursor="pointer"
      _hover={{ borderColor: 'rsViolet.400' }}
    >
      {ad.masterThumbnailUrl ? (
        <Image
          src={ad.masterThumbnailUrl}
          alt=""
          boxSize="48px"
          objectFit="cover"
          borderRadius="sm"
          flexShrink={0}
        />
      ) : (
        <Box boxSize="48px" flexShrink={0} borderRadius="sm" bg="gray.200" />
      )}
      <VStack align="flex-start" spacing={0} flex="1" minW={0}>
        <Text fontSize="11px" fontWeight="700" color="brand.ink" noOfLines={1}>
          {formatLabel ? `Master: ${formatLabel}` : 'Master'}
        </Text>
        {masterStatusBadge && (
          <Badge
            mt={1}
            fontSize="8px"
            colorScheme={adStatusBadgeColorScheme(masterStatusBadge.tone)}
            variant="subtle"
            textTransform="none"
          >
            {masterStatusBadge.label}
          </Badge>
        )}
      </VStack>
    </HStack>
  );
}

export function AdThumbnail({ ad }: { ad: ExpansionAd }) {
  const url = gridDisplayUrlFor(ad);
  // Shared table — do not duplicate stage labels here.
  const statusBadge = resolveAdStatusBadge(
    ad.status,
    ad.kind,
    ad.renderStage ?? null,
    ad.renderStageAt
  );
  // A terminal status:'failed' always outranks whatever renderStage the
  // pipeline last wrote — same precedence AdDetailModal's workflowBadge
  // already gives status==='failed' ahead of its own pipelineStatusBadge.
  // Without this override, a QC-failed ad (which KEEPS its render, so this
  // tile has a preview and reaches the badge below) reads its last-written
  // stage verbatim — 'vision QC (…)' matches STAGE_RULES' "Quality check"
  // entry, so the tile said "QUALITY CHECK" instead of "FAILED" even though
  // resolveAdStatusBadge already forces the tone to 'warn' for any failed
  // status. Same trap for a render that failed mid-upload/mid-titling with
  // a kept prior render: the label must say what happened, not where it
  // stopped. Owner requirement 2026-08-20.
  const isFailed = ad.status === 'failed';
  const qcOutline = shouldPaintQcOutline(ad);
  // Genuine status:'failed' that is NOT a QC outline (titling / crash).
  // Historical qc-failed-kept lights qcOutline and uses the QC Fail pill.
  const isNonQcFailed = isFailed && !qcOutline;
  // Scene reel identity pill takes the Master pill's corner (SceneReelBadge).
  const sceneReel = isSceneReelAd(ad);
  return (
    <Box
      borderWidth={qcOutline ? '2px' : '1px'}
      borderColor={qcOutline ? 'red.500' : 'brand.border'}
      borderRadius="md"
      overflow="hidden"
      position="relative"
      bg="gray.50"
    >
      {ad.variantKind === 'ugc' && ad.mediaId && (
        <UgcBadge mediaId={ad.mediaId} sourceMedia={ad.sourceMedia} />
      )}
      {/* Uniform 1:1 tile — non-square ads letterbox inside the shared
          frame so every card matches height. objectFit:contain (not
          cover) so we don't crop faces / products mid-frame. Mirrors
          the legacy /ads page card layout. */}
      <Box
        position="relative"
        bg="gray.900"
        style={{ aspectRatio: '1 / 1' }}
        overflow="hidden"
      >
        {url ? (
          <AdChromePreview
            aspectRatio={ad.aspectRatio}
            platformFormat={ad.platformFormat}
            ctaText={ad.ctaText}
            density="compact"
          >
            {ad.kind === 'video' ? (
              <HoverPlayVideo
                src={url}
                poster={ad.posterUrl}
                alt={ad.headline || ad.template}
              />
            ) : (
              <Image
                src={url}
                alt={ad.headline || ad.template}
                w="100%"
                h="100%"
                objectFit="contain"
                loading="lazy"
              />
            )}
          </AdChromePreview>
        ) : (
          <Box
            w="100%" h="100%"
            display="flex" alignItems="center" justifyContent="center"
            color="gray.400"
            fontSize="10px"
            textTransform="uppercase"
            letterSpacing="0.06em"
          >
            {statusBadge.inProgress
              ? statusBadge.label
              : ad.status === 'queued'    ? 'Queued'
              : ad.status === 'rendering' ? 'Rendering…'
              : ad.status === 'failed'    ? 'Render failed'
              : 'No render'}
          </Box>
        )}
        {/* PIPELINE state only — deliberately not the resting state.
            The footer below already carries the APPROVAL/lifecycle chip
            (Ready for Review / Live / Archived), which is a different
            axis. Painting an idle status word here too would put the
            same information on one tile twice and bury the thing this
            badge exists to surface. So it appears only while an ad is
            actually moving, or when it stopped badly. */}
        {qcOutline ? (
          <Badge
            position="absolute"
            top={2}
            right={2}
            colorScheme="red"
            fontSize="8px"
            textTransform="none"
            maxW="75%"
            whiteSpace="normal"
            textAlign="right"
            title={ad.visionQc?.summary || ad.visionQc?.failureDetail || undefined}
          >
            <Box as="span" textTransform="uppercase">{ad.failure?.label || 'QC Fail'}</Box>
          </Badge>
        ) : (isNonQcFailed || statusBadge.inProgress || statusBadge.tone === 'warn') && (
          <Badge
            position="absolute"
            top={2}
            right={2}
            colorScheme={adStatusBadgeColorScheme(statusBadge.tone)}
            fontSize="8px"
            textTransform="none"
            maxW="75%"
            whiteSpace="normal"
            textAlign="right"
            title={isNonQcFailed ? (ad.visionQc?.failureDetail || ad.renderErrorMessage || undefined) : (statusBadge.detail || undefined)}
          >
            <Box as="span" textTransform="uppercase">{isNonQcFailed ? 'Failed' : statusBadge.label}</Box>
            {!isNonQcFailed && statusBadge.detail && (
              <Box as="span" fontWeight="600">
                {' · '}{statusBadge.detail}
              </Box>
            )}
          </Badge>
        )}
        {/* Master-vs-derivative identity — a DIFFERENT axis from the
            pipeline/lifecycle badges above and below, so it gets its own
            corner (bottom-left) and its own visual language (solid pill,
            teal — not reused by any status tone) rather than competing with
            them for the same space. isMaster is the backend's own
            isMasterVideoAd predicate (services/campaignAdsGenerationService.js
            in liquidretail_backend) — the exact same four conditions
            findSiblingMasterAd requires of a candidate master — so this can
            never disagree with the "view master" link in AdDetailModal.
            Solid + pill mirrors UgcBadge's treatment (an identity tag, not a
            transient status), deliberately distinct from the subtle-variant
            status pills so the two kinds of badge are never visually
            ambiguous at a glance. Always false for a non-video ad, a
            derivative, or a video ad below Google's PMax duration floor —
            never rendered on those. A scene reel shows its own pill here
            instead (SceneReelBadge). */}
        {sceneReel && <SceneReelBadge summary={ad.sceneReel} position={{ bottom: 2, left: 2 }} />}
        {ad.isMaster && !sceneReel && (
          <Badge
            position="absolute"
            bottom={2}
            left={2}
            colorScheme="teal"
            variant="solid"
            fontSize="8px"
            px={1.5}
            py={0.5}
            borderRadius="full"
            textTransform="uppercase"
            letterSpacing="0.04em"
            title="Master — the paid video plate other formats are free derivatives of"
          >
            Master
          </Badge>
        )}
      </Box>
      <Box px={2} py={1.5} borderTopWidth="1px" borderColor="brand.border">
        <HStack justify="space-between" spacing={1}>
          <Text fontSize="9px" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" color="brand.muted" noOfLines={1}>
            {ad.platformFormat ? ad.platformFormat.replace(/_/g, ' ') : ad.aspectRatio}
          </Text>
          {/* 'draft' is the delivered state (routes/ads.js money-guard
              comment — the reaper must not re-render a finished ad), so a
              draft row still moving (statusBadge.inProgress, from the same
              renderStage the pipeline badge above reads) is NOT done yet —
              the top pipeline badge already names the real step, and
              repeating a static "Draft" here read as the SAME word
              whether an ad was mid-titling or fully finished, which is the
              defect this fixes. Only once the pipeline badge goes idle
              (inProgress:false) is a draft ad actually finished and
              waiting on an operator. */}
          {ad.status === 'draft' && !statusBadge.inProgress && (
            qcOutline
              ? <Badge fontSize="8px" colorScheme="red" variant="subtle">QC Fail</Badge>
              : <Badge fontSize="8px" colorScheme="purple" variant="subtle">Ready for Review</Badge>
          )}
          {ad.status === 'live' && <Badge fontSize="8px" colorScheme="green" variant="subtle">Live</Badge>}
          {ad.status === 'archived' && <Badge fontSize="8px" colorScheme="gray" variant="subtle">Archived</Badge>}
        </HStack>
        {/* Media type + intent profile — same compact-line treatment as
            the legacy /ads gallery card (pages/Ads/index.tsx). Media type
            always shows; intent profile is omitted entirely (no row, no
            stray "·") when the ad carries no funnelStage. */}
        <HStack spacing={1} fontSize="9px" color="brand.muted">
          <Text noOfLines={1}>{mediaTypeLabel(ad.kind)}</Text>
          {funnelStageLabel(ad.funnelStage) && (
            <>
              <Text>·</Text>
              <Text noOfLines={1}>{funnelStageLabel(ad.funnelStage)}</Text>
            </>
          )}
        </HStack>
      </Box>
    </Box>
  );
}
